/**
 * Dashboard Page
 * Analysis workflow and results display - matches reference implementation
 */

import { useState, useMemo, useEffect } from 'react';
import { 
  Link2, 
  FileText, 
  Play, 
  X, 
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Settings,
  Lock,
  Sparkles,
  Clock,
  Activity,
  Info,
  Tag
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../hooks/useSite';
import { useAnalysis, POST_SELECTION_THRESHOLD } from '../../hooks/useAnalysis';
import { Link } from 'react-router-dom';
import type { HubPostOverview, CategoryRecommendations, SplitImplementation, NewCategorySpec, PostConnectivityScore } from '../../lib/dashboard-api';
import { recommendationActionsApi, linksApi, implementCategoryApi, structuralTagsApi, analysisApi, type HubSpotTagWithStructural } from '../../lib/dashboard-api';

// The 4 connectivity tiers, in the same "best to worst" order as the summary boxes
type TierName = 'Content Hub' | 'Well Connected' | 'Emerging Connections' | 'Isolated';

// Helper to generate a consistent recommendation ID from the action text
function generateRecommendationId(action: string): string {
  return action
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
}

// Type for completed action tracking
interface CompletedAction {
  recommendationId: string;
  postId: number;
  completedAt: string;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { currentSite, licenseStatus, claudeStatus, isConfigured, isPro, isLoading: siteLoading } = useSite();
  const {
    isLoading: analysisLoading,
    error,
    infoMessage,
    tags,
    selectedTagIds,
    totalPosts,
    selectedPostsCount,
    activeAnalysis,
    isAnalyzing,
    crawlPhase,
    postSelectionGate,
    htmlPostSelectionSummary,
    isPaused,
    pauseReason,
    isResuming,
    latestResults,
    linkStats,
    hasResults,
    queueEntries,
    analysisHistory,
    toggleTag,
    selectAllTags,
    clearTagSelection,
    startAnalysis,
    continueWithAnalysis,
    confirmPostSelection,
    cancelPostSelectionGate,
    updateHtmlPostSelection,
    cancelAnalysis,
    resumeAnalysis,
    fetchResults,
    clearError,
  } = useAnalysis(currentSite?.id || null, currentSite?.platformId, currentSite?.cmsType);
  
  // Combined loading state - don't show content until both site and analysis data are loaded
  const isLoading = siteLoading || analysisLoading;

  const [isFullBaseline, setIsFullBaseline] = useState(true);
  const [showAllTags, setShowAllTags] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [contentHealthExpanded, setContentHealthExpanded] = useState(true);
  const [baselineDefaultSet, setBaselineDefaultSet] = useState(false);
  const [showQueueHistory, setShowQueueHistory] = useState(false);
  const [showAnalysisHistory, setShowAnalysisHistory] = useState(false);
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [fetchingSessionId, setFetchingSessionId] = useState<string | null>(null);

  // Connectivity Tier drill-down modal (click a tier box → list of posts in that tier)
  const [activeTierModal, setActiveTierModal] = useState<TierName | null>(null);
  const [tierPostsData, setTierPostsData] = useState<Record<string, PostConnectivityScore> | null>(null);
  const [tierPostsLoading, setTierPostsLoading] = useState(false);
  const [tierPostsError, setTierPostsError] = useState<string | null>(null);

  // HTML post-selection panel (gate + editable-summary modes — see useAnalysis's
  // postSelectionGate / htmlPostSelectionSummary)
  const [postSelectionMaxPostsInput, setPostSelectionMaxPostsInput] = useState<string>('');
  const [postSelectionCategories, setPostSelectionCategories] = useState<string[] | null>(null);
  const [postSelectionInitialized, setPostSelectionInitialized] = useState(false);
  const [editingPostSelection, setEditingPostSelection] = useState(false);
  const [savingPostSelection, setSavingPostSelection] = useState(false);
  
  // Strategic recommendation completion tracking (persisted to database)
  const [completedActions, setCompletedActions] = useState<CompletedAction[]>([]);
  const [completedActionsLoading, setCompletedActionsLoading] = useState(false);
  
  // Pending link counts by post ID (to show/hide View Recommendations button)
  const [pendingCountsByPost, setPendingCountsByPost] = useState<Record<string, number>>({});

  // Initialize the post-selection panel inputs whenever a new gate appears
  // (unconfigured — defaults to "most recent 100, all categories").
  useEffect(() => {
    if (postSelectionGate && !postSelectionInitialized) {
      setPostSelectionMaxPostsInput(String(Math.min(100, postSelectionGate.totalPosts)));
      setPostSelectionCategories(null);
      setPostSelectionInitialized(true);
    } else if (!postSelectionGate && postSelectionInitialized) {
      setPostSelectionInitialized(false);
    }
  }, [postSelectionGate, postSelectionInitialized]);

  // When the user opens "Edit Selection", pre-fill from the currently saved settings.
  const startEditingPostSelection = () => {
    const saved = htmlPostSelectionSummary?.savedSelection;
    setPostSelectionMaxPostsInput(saved?.maxPosts != null ? String(saved.maxPosts) : '');
    setPostSelectionCategories(saved?.categories ?? null);
    setEditingPostSelection(true);
  };

  const togglePostSelectionCategory = (allCategoryNames: string[], name: string) => {
    setPostSelectionCategories(prev => {
      // null means "all selected" — expand to the full list first so we can deselect one
      const current = prev ?? [...allCategoryNames];
      const next = current.includes(name) ? current.filter(c => c !== name) : [...current, name];
      // Selecting every category again is equivalent to "all" (null)
      return next.length === allCategoryNames.length ? null : next;
    });
  };

  // Wrapper for fetchResults with loading state and error handling
  const handleFetchResults = async (sessionId: string) => {
    console.log('🔄 handleFetchResults called with sessionId:', sessionId);
    console.log('🔄 currentSite:', currentSite);
    console.log('🔄 siteId:', currentSite?.id);
    
    if (!currentSite?.id) {
      console.error('❌ Cannot fetch results: No site ID');
      alert('Error: No site selected. Please refresh the page.');
      return;
    }
    
    setFetchingSessionId(sessionId);
    try {
      await fetchResults(sessionId);
      console.log('✅ fetchResults completed');
    } catch (err) {
      console.error('❌ fetchResults error:', err);
      alert(`Failed to fetch results: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setFetchingSessionId(null);
    }
  };

  // Set default for isFullBaseline based on client type and whether results exist.
  // HTML clients always default to full baseline (each crawl re-crawls the whole site).
  // HubSpot clients default to incremental once results exist (cost-efficient).
  useEffect(() => {
    if (!isLoading && !baselineDefaultSet) {
      setIsFullBaseline(currentSite?.cmsType === 'html' ? true : !hasResults);
      setBaselineDefaultSet(true);
    }
  }, [isLoading, hasResults, baselineDefaultSet, currentSite?.cmsType]);

  // Load completed strategic recommendation actions from database
  useEffect(() => {
    const loadCompletedActions = async () => {
      if (!currentSite?.id || !latestResults?.id) {
        setCompletedActions([]);
        return;
      }
      
      setCompletedActionsLoading(true);
      try {
        const response = await recommendationActionsApi.getCompleted(currentSite.id, latestResults.id);
        if (response.success && response.completedActions) {
          setCompletedActions(response.completedActions);
        }
      } catch (error) {
        console.error('Failed to load completed actions:', error);
      } finally {
        setCompletedActionsLoading(false);
      }
    };
    
    loadCompletedActions();
  }, [currentSite?.id, latestResults?.id]);

  // Structural tag IDs — fetched to split the tag grid into topical vs. structural sections
  const [structuralTagIds, setStructuralTagIds] = useState<string[]>([]);

  useEffect(() => {
    if (!currentSite?.id) return;
    structuralTagsApi.getAll(currentSite.id)
      .then(res => { if (res.success) setStructuralTagIds(res.structuralTagIds || []); })
      .catch(() => {});
  }, [currentSite?.id]);

  // Load pending link recommendation counts by post
  useEffect(() => {
    const loadPendingCounts = async () => {
      if (!currentSite?.id) {
        setPendingCountsByPost({});
        return;
      }
      
      try {
        const response = await linksApi.getPendingCountsByPost(currentSite.id);
        if (response.success && response.pendingByPost) {
          setPendingCountsByPost(response.pendingByPost);
        }
      } catch (error) {
        console.error('Failed to load pending counts:', error);
      }
    };
    
    loadPendingCounts();
  }, [currentSite?.id]);

  // Reset the cached per-post connectivity data whenever the site or the latest
  // analysis changes, so a stale list can't be shown under a newly-run analysis.
  useEffect(() => {
    setTierPostsData(null);
    setTierPostsError(null);
  }, [currentSite?.id, latestResults?.id]);

  // Lazy-load per-post connectivity scores (title/category/tier) the first time
  // any tier box is clicked. Cached afterward — switching between tiers just
  // re-filters the same map, no refetch.
  useEffect(() => {
    if (!activeTierModal || !currentSite?.id || tierPostsData !== null) return;

    let cancelled = false;
    setTierPostsLoading(true);
    setTierPostsError(null);

    analysisApi.getContentAnalysisData(currentSite.id, latestResults?.id)
      .then(res => {
        if (cancelled) return;
        if (res.success && res.connectivityScores) {
          setTierPostsData(res.connectivityScores);
        } else {
          setTierPostsData({});
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load connectivity scores for tier modal:', err);
        setTierPostsError('Failed to load posts for this tier. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setTierPostsLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeTierModal, currentSite?.id, latestResults?.id, tierPostsData]);

  // Handler for toggling post completion - persists to database
  const handleTogglePostComplete = async (recommendationId: string, postId: number) => {
    if (!currentSite?.id) return;
    
    try {
      const response = await recommendationActionsApi.markComplete(currentSite.id, {
        recommendationId,
        analysisId: latestResults?.id,
        postId,
      });
      
      if (response.success) {
        // Update local state based on whether it was toggled on or off
        setCompletedActions(prev => {
          const exists = prev.some(a => a.recommendationId === recommendationId && a.postId === postId);
          if (exists) {
            // Remove it (toggled off)
            return prev.filter(a => !(a.recommendationId === recommendationId && a.postId === postId));
          } else {
            // Add it (toggled on)
            return [...prev, { recommendationId, postId, completedAt: new Date().toISOString() }];
          }
        });
      }
    } catch (error) {
      console.error('Failed to toggle completion status:', error);
    }
  };

  // Helper to get completed post IDs for a specific recommendation
  const getCompletedPostsForRecommendation = (recommendationId: string): number[] => {
    return completedActions
      .filter(a => a.recommendationId === recommendationId)
      .map(a => a.postId);
  };

  // Get tier limits based on license
  const tierLimits = useMemo(() => {
    const tier = licenseStatus?.tier?.toLowerCase() || 'free';
    if (tier.includes('pro')) {
      return {
        maxPostsToAnalyze: -1, // unlimited
        maxLinkRecommendations: 500,
        analysesPerMonth: -1, // unlimited
        resultsHistory: true,
        categorySelection: true,
        incrementalAnalysis: true,
      };
    }
    return {
      maxPostsToAnalyze: 50,
      maxLinkRecommendations: 25,
      analysesPerMonth: 1,
      resultsHistory: false,
      categorySelection: false,
      incrementalAnalysis: false,
    };
  }, [licenseStatus?.tier]);

  // Stats from latest results (only Posts Analyzed and Link Recommendations)
  const stats = useMemo(() => {
    return [
      { 
        name: 'Posts Analyzed', 
        value: latestResults?.postsAnalyzed?.toString() || '0', 
        icon: FileText, 
        color: '#60a5fa' // blue-400
      },
      { 
        name: 'Link Recommendations', 
        value: linkStats?.total?.toString() || '0', 
        icon: Link2, 
        color: '#f472b6' // pink-400
      },
    ];
  }, [latestResults, linkStats]);

  // Handle analysis start
  const handleStartAnalysis = async () => {
    try {
      await startAnalysis(isFullBaseline);
    } catch {
      // Error is handled in the hook
    }
  };

  // Split tags into selectable topical tags and read-only structural tags
  const topicalTags = tags.filter(t => !structuralTagIds.includes(t.id));
  const structuralTags = tags.filter(t => structuralTagIds.includes(t.id));
  const displayedTags = showAllTags ? topicalTags : topicalTags.slice(0, 12);

  // HTML / No-CMS client — no tag selection, no CMS write-back
  const isHtmlClient = currentSite?.cmsType === 'html';

  // Loading state - show spinner while site data is loading
  if (isLoading && currentSite) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#ffdc18] animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // No site selected
  if (!currentSite) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">
            Welcome{user?.name ? `, ${user.name}` : ''}! 👋
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Connect a site to get started.
          </p>
        </div>
        <div className="glass rounded-xl p-8 text-center animate-slide-up">
          <div className="w-16 h-16 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-4">
            <Link2 className="w-8 h-8 text-[#ffdc18]" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No Site Connected</h2>
          <p className="text-[var(--text-secondary)] max-w-md mx-auto mb-6">
            Connect your blog to start analyzing your content.
          </p>
          <Link
            to="/connect-html-site"
            className="px-6 py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg transition-colors inline-block"
          >
            Connect your blog by URL
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Setup Required Banner - Non-dismissible */}
      {!isConfigured && currentSite && (
        <div className="p-4 bg-[#f59e0b]/15 border border-[#f59e0b]/40 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#f59e0b]/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-[#f59e0b]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#fbbf24]">Setup Required</h3>
              <p className="text-sm text-white/70 mt-1">
                {licenseStatus?.status !== 'active' && claudeStatus?.status !== 'valid' 
                  ? 'Activate your license key and configure your Claude API key to start analyzing content.'
                  : licenseStatus?.status !== 'active'
                    ? 'Activate your license key to start analyzing content.'
                    : 'Configure your Claude API key to enable AI-powered analysis.'
                }
              </p>
              <Link 
                to="/settings"
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg transition-colors text-sm"
              >
                <Settings className="w-4 h-4" />
                Complete Setup in Settings
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold">
          Welcome{user?.name ? `, ${user.name}` : ''}! 👋
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">
          Managing {currentSite?.siteName || 'your site'}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-[var(--color-error)]" />
            <span className="text-[var(--color-error)]">{error}</span>
          </div>
          <button onClick={clearError} className="p-1 hover:bg-[var(--color-error)]/20 rounded">
            <X className="w-4 h-4 text-[var(--color-error)]" />
          </button>
        </div>
      )}

      {/* Info — e.g. no content changes detected on incremental analysis */}
      {infoMessage && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <span className="text-amber-100">{infoMessage}</span>
          </div>
          <button onClick={clearError} className="p-1 hover:bg-amber-500/20 rounded">
            <X className="w-4 h-4 text-amber-400" />
          </button>
        </div>
      )}

      {/* Stats */}
      {hasResults && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.name}
                className="glass rounded-xl p-5 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-white/70">{stat.name}</p>
                    <p className="text-3xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${stat.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Analysis Section */}
      <div className="glass rounded-xl p-6 animate-slide-up delay-200">
        <h2 className="text-lg font-semibold mb-4">Baseline Analysis</h2>
        
        {/* Paused Analysis Banner (billing error, throttling, overload, etc.) */}
        {isPaused && activeAnalysis && pauseReason && (
          <AnalysisPausedBanner 
            pauseReason={pauseReason}
            onResume={resumeAnalysis}
            onCancel={cancelAnalysis}
            isResuming={isResuming}
            sessionId={activeAnalysis.sessionId}
            progress={activeAnalysis.progress || 0}
            progressMessage={activeAnalysis.progressMessage}
          />
        )}

        {/* Two-step progress stepper (HTML clients: crawl → analysis).
            Show whenever there is a crawl phase and no active BKA analysis.
            isAnalyzing is intentionally NOT excluded here — it is true during the
            crawl phase too, and hiding the stepper at that point leaves the user
            with zero feedback while the crawl runs in the background. */}
        {crawlPhase && !activeAnalysis && (
          <div className="space-y-3">
            {/* Step 1 — Blog Crawl */}
            {(() => {
              const crawlActive  = ['queued', 'crawling', 'extracting'].includes(crawlPhase.status);
              const crawlDone    = crawlPhase.status === 'done';
              const crawlError   = crawlPhase.status === 'error';
              const crawlPct     = crawlPhase.postsFound > 0
                ? Math.round((crawlPhase.postsExtracted / crawlPhase.postsFound) * 100)
                : 0;

              return (
                <div className={`border rounded-lg overflow-hidden ${
                  crawlError ? 'border-red-500/30 bg-red-500/5'
                  : crawlDone ? 'border-green-500/30 bg-green-500/5'
                  : 'border-blue-500/30 bg-blue-500/5'
                }`}>
                  <div className={`px-4 py-3 border-b flex items-center justify-between ${
                    crawlError ? 'bg-red-500/10 border-red-500/20'
                    : crawlDone ? 'bg-green-500/10 border-green-500/20'
                    : 'bg-blue-500/10 border-blue-500/20'
                  }`}>
                    <div className="flex items-center gap-2">
                      {crawlError
                        ? <AlertTriangle className="w-4 h-4 text-red-400" />
                        : crawlDone
                          ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                          : <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                      }
                      <span className={`font-medium text-sm ${
                        crawlError ? 'text-red-400' : crawlDone ? 'text-green-400' : 'text-blue-400'
                      }`}>
                        Step 1 — Blog Crawl
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                      crawlError ? 'bg-red-500/20 text-red-400'
                      : crawlDone ? 'bg-green-500/20 text-green-400'
                      : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {crawlPhase.status}
                    </span>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-white/60">Posts Found</span>
                        <p className="font-medium">{crawlPhase.postsFound}</p>
                      </div>
                      <div>
                        <span className="text-white/60">Posts Extracted</span>
                        <p className="font-medium">{crawlPhase.postsExtracted}</p>
                      </div>
                    </div>
                    {crawlActive && crawlPhase.postsFound > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-white/50 mb-1">
                          <span>Extraction progress</span>
                          <span>{crawlPct}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-400 rounded-full transition-all duration-500"
                            style={{ width: `${crawlPct}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {crawlError && crawlPhase.error && (
                      <p className="text-sm text-[var(--color-error)]">{crawlPhase.error}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Step 2 — AI Analysis */}
            {(() => {
              // Stepper is only rendered when !activeAnalysis && !isAnalyzing, so:
              // step2Pending = crawl not done yet
              // step2Ready   = crawl done, no results yet
              // step2Done    = crawl done, results exist
              const step2Pending = crawlPhase.status !== 'done';
              const step2Ready   = crawlPhase.status === 'done' && !hasResults;
              const step2Done    = crawlPhase.status === 'done' && hasResults;

              const analysisLabel = step2Done  ? 'complete'
                                  : step2Ready ? 'ready'
                                  : 'pending';

              return (
                <div className={`border rounded-lg overflow-hidden ${
                  step2Done  ? 'border-green-500/30 bg-green-500/5'
                  : step2Ready ? 'border-blue-500/30 bg-blue-500/5'
                  : 'border-white/10 bg-white/3'
                }`}>
                  <div className={`px-4 py-3 border-b flex items-center justify-between ${
                    step2Done  ? 'bg-green-500/10 border-green-500/20'
                    : step2Ready ? 'bg-blue-500/10 border-blue-500/20'
                    : 'bg-white/5 border-white/10'
                  }`}>
                    <div className="flex items-center gap-2">
                      {step2Done
                        ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                        : step2Ready
                          ? <Sparkles className="w-4 h-4 text-blue-400" />
                          : <Clock className="w-4 h-4 text-white/40" />
                      }
                      <span className={`font-medium text-sm ${
                        step2Done  ? 'text-green-400'
                        : step2Ready ? 'text-blue-400'
                        : 'text-white/40'
                      }`}>
                        Step 2 — AI Analysis
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      step2Done  ? 'bg-green-500/20 text-green-400'
                      : step2Ready ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-white/10 text-white/40'
                    }`}>
                      {analysisLabel}
                    </span>
                  </div>

                  {/* Waiting for crawl */}
                  {step2Pending && (
                    <div className="p-4">
                      <p className="text-sm text-white/40">Waiting for crawl to complete…</p>
                    </div>
                  )}

                  {/* Crawl done, analysis not yet run */}
                  {step2Ready && (
                    <div className="p-4 space-y-3">
                      {postSelectionGate ? (
                        /* More than 100 posts found and no selection saved yet — let the
                           user narrow down before this (or any future) run sends posts to BKA. */
                        (() => {
                          const allCategoryNames = postSelectionGate.categories.map(c => c.name);
                          const maxPostsValue = postSelectionMaxPostsInput.trim() === ''
                            ? null
                            : parseInt(postSelectionMaxPostsInput, 10);
                          const categoryMatchCount = postSelectionCategories === null
                            ? postSelectionGate.totalPosts
                            : postSelectionGate.categories
                                .filter(c => postSelectionCategories.includes(c.name))
                                .reduce((sum, c) => sum + c.count, 0);
                          const previewCount = maxPostsValue != null && maxPostsValue > 0
                            ? Math.min(categoryMatchCount, maxPostsValue)
                            : categoryMatchCount;
                          const noCategoriesSelected = postSelectionCategories !== null && postSelectionCategories.length === 0;

                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-sm">Select Posts to Analyze</h4>
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded">
                                  {postSelectionGate.totalPosts.toLocaleString()} posts found
                                </span>
                              </div>
                              <p className="text-sm text-white/60">
                                This blog has more posts than usual. Choose how many of the most recent posts
                                to analyze and/or limit to specific categories — you can change this anytime.
                              </p>

                              <div>
                                <label className="block text-sm font-medium mb-1">Analyze most recent</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min={1}
                                    max={postSelectionGate.totalPosts}
                                    value={postSelectionMaxPostsInput}
                                    onChange={(e) => setPostSelectionMaxPostsInput(e.target.value)}
                                    placeholder="No limit"
                                    className="w-28 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] transition-colors text-sm"
                                  />
                                  <span className="text-sm text-white/60">posts (leave blank for no limit)</span>
                                </div>
                              </div>

                              {allCategoryNames.length > 1 && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium">Categories</label>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setPostSelectionCategories(null)}
                                        className="px-2 py-1 text-xs bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                                      >
                                        Select All
                                      </button>
                                      <button
                                        onClick={() => setPostSelectionCategories([])}
                                        className="px-2 py-1 text-xs bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                                      >
                                        Clear All
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1">
                                    {postSelectionGate.categories.map(cat => {
                                      const isSelected = postSelectionCategories === null || postSelectionCategories.includes(cat.name);
                                      return (
                                        <label
                                          key={cat.name}
                                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                                            isSelected
                                              ? 'bg-[#ffdc18]/10 border border-[#ffdc18]/30'
                                              : 'bg-[var(--bg-tertiary)] border border-transparent'
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => togglePostSelectionCategory(allCategoryNames, cat.name)}
                                            className="w-4 h-4 rounded border-[var(--bg-tertiary)] text-[#ffdc18] focus:ring-[#ffdc18]"
                                          />
                                          <span className="flex-1 min-w-0 truncate">{cat.name}</span>
                                          <span className="text-xs text-white/50">{cat.count}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              <p className="text-xs text-white/50">
                                ~{previewCount.toLocaleString()} of {postSelectionGate.totalPosts.toLocaleString()} posts will be analyzed.
                                {postSelectionCategories !== null && postSelectionCategories.length > 0 && ' (Category counts may overlap if a post has multiple categories.)'}
                              </p>

                              <button
                                onClick={() => confirmPostSelection(
                                  maxPostsValue != null && maxPostsValue > 0 ? maxPostsValue : null,
                                  postSelectionCategories,
                                )}
                                disabled={noCategoriesSelected}
                                className="w-full py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-semibold text-base rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              >
                                <Sparkles className="w-5 h-5" />
                                Confirm & Start Analysis
                              </button>
                              <button
                                onClick={cancelPostSelectionGate}
                                className="w-full py-2 text-sm text-white/50 hover:text-white/70 transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <p className="text-sm text-white/60">
                            {crawlPhase.postsExtracted} post{crawlPhase.postsExtracted !== 1 ? 's' : ''} crawled and ready.
                            Start the AI analysis to get link recommendations and content insights.
                          </p>
                          <button
                            onClick={() => continueWithAnalysis(crawlPhase.jobId!)}
                            className="w-full py-3 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-semibold text-base rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <Sparkles className="w-5 h-5" />
                            Continue with Analysis
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Analysis complete */}
                  {step2Done && (
                    <div className="p-4">
                      <p className="text-sm text-green-400/70">Analysis complete — results are ready below.</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Active Analysis Progress — shown for ALL clients (HubSpot + HTML) while
            analysis is running. Uses activeAnalysis as the primary gate so this
            appears immediately when the queue entry exists, even if the isAnalyzing
            polling flag hasn't updated yet. */}
        {!!activeAnalysis && !isPaused && (
          <div className="space-y-4">
            <p className="text-[var(--text-secondary)]">
              Analysis in progress. You can monitor the status below or close this page - the analysis will continue on the server.
            </p>

            {/* Analysis Queue Status Card */}
            <div className="border border-[#ffdc18]/30 bg-[#ffdc18]/5 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 bg-[#ffdc18]/10 border-b border-[#ffdc18]/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#ffdc18]" />
                  <span className="font-medium text-[#ffdc18]">Analysis Queue Status</span>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  activeAnalysis.status === 'completed' ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]' :
                  activeAnalysis.status === 'failed' ? 'bg-[var(--color-error)]/20 text-[var(--color-error)]' :
                  activeAnalysis.status === 'cancelled' ? 'bg-gray-500/20 text-gray-400' :
                  'bg-[#ffdc18]/20 text-[#ffdc18]'
                }`}>
                  {activeAnalysis.status === 'uploading' ? 'Uploading' :
                   activeAnalysis.status === 'analyzing' ? 'Analyzing' :
                   activeAnalysis.status === 'processing' ? 'Processing' :
                   activeAnalysis.status.charAt(0).toUpperCase() + activeAnalysis.status.slice(1)}
                </span>
              </div>
              
              {/* Details Table */}
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/70">Session ID</span>
                    <p className="font-mono text-xs mt-1 bg-[var(--bg-tertiary)] px-2 py-1 rounded truncate">
                      {activeAnalysis.sessionId}
                    </p>
                  </div>
                  <div>
                    <span className="text-white/70">Type</span>
                    <p className="mt-1">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                        {isFullBaseline ? 'Baseline' : 'Incremental'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="text-white/70">Posts</span>
                    <p className="mt-1 font-medium">
                      {activeAnalysis.postsUploaded || 0} / {activeAnalysis.postsCount || '?'} uploaded
                    </p>
                  </div>
                  <div>
                    <span className="text-white/70">Started</span>
                    <p className="mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {activeAnalysis.startedAt ? new Date(activeAnalysis.startedAt).toLocaleTimeString() : 'Just now'}
                    </p>
                  </div>
                </div>

                {/* Structural excluded notice */}
                {(activeAnalysis.structuralExcluded ?? 0) > 0 && (
                  <div className="flex items-center gap-2 text-xs text-[#06b6d4] bg-[#06b6d4]/10 border border-[#06b6d4]/30 rounded px-3 py-2">
                    <Info className="w-3 h-3 flex-shrink-0" />
                    {activeAnalysis.structuralExcluded} post{activeAnalysis.structuralExcluded !== 1 ? 's' : ''} excluded from analysis (structural tags only).
                  </div>
                )}

                {/* Progress Bar */}
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-secondary)]">{activeAnalysis.progressMessage || 'Processing...'}</span>
                    <span className="font-medium">{activeAnalysis.progress || 0}%</span>
                  </div>
                  <div className="w-full bg-[var(--bg-tertiary)] rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${
                        activeAnalysis.status === 'completed' 
                          ? 'bg-[var(--color-success)]' 
                          : 'bg-gradient-to-r from-[#ffdc18] to-[#e6c616]'
                      }`}
                      style={{ width: `${activeAnalysis.progress || 0}%` }}
                    />
                  </div>
                  {activeAnalysis.progressDetails && (
                    <p className="text-xs text-white/50 italic pt-0.5">{activeAnalysis.progressDetails}</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 py-3 bg-[var(--bg-tertiary)]/50 border-t border-[var(--bg-tertiary)] flex gap-3">
                {activeAnalysis.status === 'completed' ? (
                  <button
                    onClick={() => handleFetchResults(activeAnalysis.sessionId)}
                    disabled={fetchingSessionId === activeAnalysis.sessionId}
                    className="flex-1 py-2 bg-[var(--color-success)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-4 h-4 ${fetchingSessionId === activeAnalysis.sessionId ? 'animate-spin' : ''}`} />
                    {fetchingSessionId === activeAnalysis.sessionId ? 'Fetching...' : 'Fetch Results'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Check Progress
                    </button>
                    <button
                      onClick={cancelAnalysis}
                      className="px-4 py-2 bg-[var(--color-error)]/10 text-[var(--color-error)] rounded-lg hover:bg-[var(--color-error)]/20 transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Start Analysis Form — hidden while any analysis is active or queued */}
        {!isAnalyzing && !activeAnalysis && !isPaused && (
          <div className="space-y-6">
            <p className="text-[var(--text-secondary)]">
              Run a comprehensive AI-powered analysis of your blog content to discover strategic insights and internal linking opportunities.
            </p>

            {/* Last Analysis Info */}
            {latestResults && (
              <p className="text-sm text-white/70">
                Last analysis: <strong>{new Date(latestResults.createdAt).toLocaleString()}</strong>
                {latestResults.postsAnalyzed && ` • ${latestResults.postsAnalyzed} posts analyzed`}
                {' | '}<a href="#results" className="text-[#ffdc18] hover:underline">View Results</a>
              </p>
            )}

            {/* Tag Selection — HTML clients get a different panel */}
            <div className="space-y-4">
              {isHtmlClient ? (
                /* ── HTML / No-CMS client ── */
                <div className="space-y-4">
                  <h3 className="font-semibold">Blog Source</h3>

                  {/* Blog archive URL display */}
                  <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg flex items-start gap-3">
                    <Info className="w-5 h-5 text-[#60a5fa] flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 min-w-0">
                      <p className="text-sm font-medium text-white/80">Blog Archive URL</p>
                      <p className="text-sm text-[#60a5fa] break-all">
                        {currentSite?.blogArchiveUrl || currentSite?.siteUrl || '—'}
                      </p>
                      <p className="text-xs text-white/50 mt-1">
                        Posts will be crawled from this URL. To change it,{' '}
                        <Link to="/settings" className="text-[#ffdc18] hover:underline">update it in Settings</Link>.
                      </p>
                    </div>
                  </div>

                  {/* Crawl info */}
                  <div className="p-4 bg-[#60a5fa]/10 border-l-4 border-[#60a5fa] rounded-r-lg">
                    <p className="text-sm">
                      <strong>How it works:</strong> The crawler will discover all posts from your blog archive
                      and send them for AI-powered analysis. Categories are discovered automatically during
                      each crawl. Blogs with more than 100 posts can be narrowed down to a subset below.
                    </p>
                  </div>

                  {/* Post selection summary + editor — only relevant once we know about a
                      completed crawl (categories/counts come from it). Not shown while the
                      blocking gate above is active for a first-time large-blog selection. */}
                  {htmlPostSelectionSummary && htmlPostSelectionSummary.totalPosts > POST_SELECTION_THRESHOLD && !postSelectionGate && (
                    <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg space-y-3">
                      {editingPostSelection ? (
                        (() => {
                          const allCategoryNames = htmlPostSelectionSummary.categories.map(c => c.name);
                          const noCategoriesSelected = postSelectionCategories !== null && postSelectionCategories.length === 0;
                          return (
                            <>
                              <h4 className="font-semibold text-sm">Edit Post Selection</h4>
                              <div>
                                <label className="block text-sm font-medium mb-1">Analyze most recent</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min={1}
                                    max={htmlPostSelectionSummary.totalPosts}
                                    value={postSelectionMaxPostsInput}
                                    onChange={(e) => setPostSelectionMaxPostsInput(e.target.value)}
                                    placeholder="No limit"
                                    className="w-28 px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[#ffdc18] transition-colors text-sm"
                                  />
                                  <span className="text-sm text-white/60">posts (leave blank for no limit)</span>
                                </div>
                              </div>
                              {allCategoryNames.length > 1 && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium">Categories</label>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setPostSelectionCategories(null)}
                                        className="px-2 py-1 text-xs bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                                      >
                                        Select All
                                      </button>
                                      <button
                                        onClick={() => setPostSelectionCategories([])}
                                        className="px-2 py-1 text-xs bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                                      >
                                        Clear All
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1">
                                    {htmlPostSelectionSummary.categories.map(cat => {
                                      const isSelected = postSelectionCategories === null || postSelectionCategories.includes(cat.name);
                                      return (
                                        <label
                                          key={cat.name}
                                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                                            isSelected
                                              ? 'bg-[#ffdc18]/10 border border-[#ffdc18]/30'
                                              : 'bg-[var(--bg-secondary)] border border-transparent'
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => togglePostSelectionCategory(allCategoryNames, cat.name)}
                                            className="w-4 h-4 rounded border-[var(--bg-tertiary)] text-[#ffdc18] focus:ring-[#ffdc18]"
                                          />
                                          <span className="flex-1 min-w-0 truncate">{cat.name}</span>
                                          <span className="text-xs text-white/50">{cat.count}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button
                                  disabled={noCategoriesSelected || savingPostSelection}
                                  onClick={async () => {
                                    setSavingPostSelection(true);
                                    try {
                                      const n = postSelectionMaxPostsInput.trim() === '' ? null : parseInt(postSelectionMaxPostsInput, 10);
                                      await updateHtmlPostSelection(n != null && n > 0 ? n : null, postSelectionCategories);
                                      setEditingPostSelection(false);
                                    } finally {
                                      setSavingPostSelection(false);
                                    }
                                  }}
                                  className="px-4 py-2 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {savingPostSelection ? 'Saving…' : 'Save Selection'}
                                </button>
                                <button
                                  onClick={() => setEditingPostSelection(false)}
                                  className="px-4 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                              <p className="text-xs text-white/50">
                                Takes effect the next time analysis runs — it doesn't change posts already analyzed.
                              </p>
                            </>
                          );
                        })()
                      ) : (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm text-white/70">
                            {htmlPostSelectionSummary.savedSelection.configured ? (
                              <>
                                Analyzing{' '}
                                {htmlPostSelectionSummary.savedSelection.maxPosts
                                  ? <>up to <strong>{htmlPostSelectionSummary.savedSelection.maxPosts}</strong> most recent posts</>
                                  : <>all posts</>}
                                {htmlPostSelectionSummary.savedSelection.categories && htmlPostSelectionSummary.savedSelection.categories.length > 0 && (
                                  <> across <strong>{htmlPostSelectionSummary.savedSelection.categories.length}</strong> of {htmlPostSelectionSummary.categories.length} categories</>
                                )}
                                {' '}(of {htmlPostSelectionSummary.totalPosts.toLocaleString()} found).
                              </>
                            ) : (
                              <>All {htmlPostSelectionSummary.totalPosts.toLocaleString()} crawled posts will be analyzed.</>
                            )}
                          </p>
                          <button
                            onClick={startEditingPostSelection}
                            className="flex-shrink-0 text-sm text-[#ffdc18] hover:underline"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
              /* ── HubSpot / standard CMS clients ── */
              <>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">Select Tags to Analyze</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-[#ffdc18]/20 text-[#ffdc18] rounded">PRO</span>
              </div>

              {isPro ? (
                <>
                  <p className="text-sm text-white/70">
                    All tags are selected by default. Deselect tags you don't want to analyze, or use "Clear All" to start with a clean slate.
                    <span className="text-[#60a5fa]"> Only English posts will be analyzed.</span>
                  </p>

                  {/* Tag Selection Controls */}
                  <div className="flex items-center justify-between p-3 bg-[var(--bg-tertiary)] rounded-lg">
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllTags}
                        className="px-3 py-1.5 text-sm bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearTagSelection}
                        className="px-3 py-1.5 text-sm bg-[var(--bg-secondary)] rounded hover:bg-[var(--bg-secondary)]/80 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    <span className={`text-sm ${selectedTagIds.length === 0 ? 'text-[var(--color-error)]' : 'text-white/70'}`}>
                      <strong>{selectedTagIds.length}</strong> tags selected (<strong>{selectedPostsCount.toLocaleString()}</strong> English posts)
                    </span>
                  </div>

                  {/* Tag Grid */}
                  {isLoading ? (
                    <div className="flex items-center justify-center p-8 text-white/70">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Loading tags...
                    </div>
                  ) : tags.length === 0 ? (
                    <div className="p-6 bg-[var(--bg-tertiary)] rounded-lg">
                      <p className="text-white/70 mb-2">
                        <strong>No tags found.</strong>
                      </p>
                      <p className="text-sm text-white/70">
                        Unable to load tags from your HubSpot blog. This may require HubSpot OAuth authorization.
                        Check browser console for details.
                      </p>
                      <p className="text-xs text-white/70 mt-2">
                        Portal ID: {currentSite?.platformId || 'Not set'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto p-1">
                        {displayedTags.map(tag => {
                          const isUncategorized = tag.id === '__uncategorized__';
                          const isSelected = selectedTagIds.includes(tag.id);
                          const percentage = totalPosts > 0 ? Math.round((tag.postCount / totalPosts) * 100) : 0;
                          const englishCount = tag.englishCount ?? tag.postCount;
                          const nonEnglishCount = tag.nonEnglishCount ?? 0;
                          const hasNonEnglish = nonEnglishCount > 0;
                          
                          return (
                            <label
                              key={tag.id}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                isSelected 
                                  ? isUncategorized
                                    ? 'bg-white/10 border border-white/30'
                                    : 'bg-[#ffdc18]/10 border border-[#ffdc18]/30'
                                  : 'bg-[var(--bg-tertiary)] border border-transparent hover:border-[var(--bg-tertiary)]'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTag(tag.id)}
                                className="w-4 h-4 rounded border-[var(--bg-tertiary)] text-[#ffdc18] focus:ring-[#ffdc18]"
                              />
                              <div className="flex-1 min-w-0">
                                <span className={`font-medium truncate block ${isUncategorized ? 'italic text-white/70' : ''}`}>
                                  {tag.name}
                                </span>
                                <span className="text-xs text-white/70">
                                  {englishCount} EN{hasNonEnglish && (
                                    <span className="text-white/40"> + {nonEnglishCount} other</span>
                                  )} ({percentage}%)
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      
                      {topicalTags.length > 12 && (
                        <button
                          onClick={() => setShowAllTags(!showAllTags)}
                          className="flex items-center gap-1 text-sm text-[#ffdc18] hover:underline"
                        >
                          {showAllTags ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              Show Less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              Show All {topicalTags.length} Tags
                            </>
                          )}
                        </button>
                      )}

                      {/* Structural Tags — excluded from analysis */}
                      {structuralTags.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 mb-2">
                            <Tag className="w-4 h-4 text-white/40" />
                            <span className="text-sm font-medium text-white/40">Structural Tags — Excluded from Analysis</span>
                          </div>
                          <p className="text-xs text-white/30 mb-3">
                            These tags are designated as structural (navigation/taxonomy) in{' '}
                            <Link to="/settings" className="text-white/50 hover:text-white/70 underline">App Settings</Link>.
                            Posts tagged exclusively with structural tags are not uploaded to analysis.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {structuralTags.map(tag => {
                              const englishCount = tag.englishCount ?? tag.postCount;
                              return (
                                <div
                                  key={tag.id}
                                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 opacity-50 cursor-not-allowed"
                                >
                                  <div className="w-4 h-4 rounded border border-white/20 bg-white/5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium truncate block text-white/50 text-sm">{tag.name}</span>
                                    <span className="text-xs text-white/30">{englishCount} EN posts</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Tip */}
                  <div className="p-4 bg-[#60a5fa]/10 border-l-4 border-[#60a5fa] rounded-r-lg">
                    <p className="text-sm">
                      <strong>💡 Tip:</strong> Start with your most important tags to manage API costs. 
                      Use <strong>Incremental Analysis</strong> to add more tags over time without re-analyzing existing content.
                    </p>
                  </div>
                </>
              ) : (
                /* FREE Tier - Show upgrade message */
                <div className="p-6 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Lock className="w-6 h-6 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
                    <div className="space-y-3">
                      <h4 className="font-semibold">Upgrade to PRO for Tag Selection</h4>
                      <p className="text-sm text-[var(--text-secondary)]">
                        Control your analysis costs by selecting specific tags to analyze. Perfect for large sites with diverse content.
                      </p>
                      <ul className="text-sm space-y-1">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />
                          Select specific tags to analyze
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />
                          Phased rollout for large content libraries
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />
                          Cumulative analysis with incremental updates
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />
                          Better cost management for API usage
                        </li>
                      </ul>
                      <a 
                        href="https://vizzex.ai" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-gray-900 font-medium rounded-lg hover:bg-[#e6c616] transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Upgrade to PRO
                      </a>
                      <p className="text-xs text-white/70 italic mt-2">
                        Free tier will analyze all tags in your blog.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              </>
              )} {/* end isHtmlClient ternary */}
            </div>

            {/* Analysis Type Checkbox - right above button */}
            <div className="space-y-2 text-center">
              <label className={`inline-flex items-center gap-3 cursor-pointer ${!tierLimits.incrementalAnalysis ? 'opacity-60 cursor-not-allowed' : ''}`}>
                <input
                  type="checkbox"
                  checked={isFullBaseline}
                  onChange={(e) => {
                    if (tierLimits.incrementalAnalysis || e.target.checked) {
                      setIsFullBaseline(e.target.checked);
                    }
                  }}
                  disabled={!tierLimits.incrementalAnalysis}
                  className="w-5 h-5 rounded border-[var(--bg-tertiary)] text-[#ffdc18] focus:ring-[#ffdc18] cursor-pointer"
                />
                <span className="font-medium">Run Full Baseline Analysis</span>
                {!tierLimits.incrementalAnalysis && (
                  <span className="text-xs text-[var(--color-warning)]">(Required for Free tier)</span>
                )}
              </label>
              <p className="text-sm text-white/70">
                {!tierLimits.incrementalAnalysis 
                  ? 'Checked: Full re-analysis of all posts (hierarchical). Unchecked: Only analyze new/changed posts (incremental) - available in PRO version.'
                  : 'Checked: Full re-analysis of all posts (hierarchical). Unchecked: Only analyze new/changed posts (incremental).'
                }
              </p>
            </div>

            {/* Run Analysis Button - full width, centered */}
            <button
              onClick={handleStartAnalysis}
              disabled={!isConfigured || isLoading || (!isHtmlClient && isPro && selectedTagIds.length === 0)}
              className="w-full py-4 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-semibold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              title={!isConfigured ? 'Complete setup in Settings to run analysis' : undefined}
            >
              {isLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Play className="w-6 h-6" />
              )}
              {isFullBaseline ? 'Run Baseline Analysis' : 'Run Incremental Analysis'}
            </button>
          </div>
        )}
      </div>

      {/* Analysis Queue Status */}
      {queueEntries.length > 0 && (
        <div className="glass rounded-xl p-6 animate-slide-up delay-250">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#ffdc18]" />
              <h2 className="text-lg font-semibold">Analysis Queue Status</h2>
            </div>
            {isPro && queueEntries.length > 1 && (
              <button
                onClick={() => setShowQueueHistory(!showQueueHistory)}
                className="flex items-center gap-1 text-sm text-[#ffdc18] hover:underline"
              >
                {showQueueHistory ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {queueEntries.length - 1} Older
                  </>
                )}
              </button>
            )}
          </div>
          <p className="text-sm text-white/70 mb-4">
            Recent analysis queue entries. "Processing" entries may need attention if stuck.
          </p>

          {/* Queue Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bg-tertiary)]">
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Session ID</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Type</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Status</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Progress</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Started</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(showQueueHistory ? queueEntries : queueEntries.slice(0, 1)).map((entry, index) => (
                  <tr 
                    key={entry.id} 
                    className={`border-b border-[var(--bg-tertiary)]/50 ${index > 0 ? 'opacity-75' : ''}`}
                  >
                    <td className="py-3 px-3">
                      <code className="text-xs bg-[var(--bg-tertiary)] px-2 py-1 rounded break-all select-all" title="Click to select">
                        {entry.sessionId}
                      </code>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entry.analysisType === 'Incremental' 
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {entry.analysisType}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entry.status === 'completed' ? 'bg-[var(--color-success)]/20 text-[var(--color-success)]' :
                        entry.status === 'failed' ? 'bg-[var(--color-error)]/20 text-[var(--color-error)]' :
                        entry.status === 'cancelled' ? 'bg-gray-500/20 text-gray-400' :
                        entry.isStuck ? 'bg-[var(--color-error)]/20 text-[var(--color-error)]' :
                        'bg-[#ffdc18]/20 text-[#ffdc18]'
                      }`}>
                        {entry.isStuck ? 'Stuck' : entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-[var(--bg-tertiary)] rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              entry.status === 'completed' ? 'bg-[var(--color-success)]' :
                              entry.status === 'failed' ? 'bg-[var(--color-error)]' :
                              'bg-[#ffdc18]'
                            }`}
                            style={{ width: `${entry.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-white/70">{entry.progress}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs text-white/70">
                      {new Date(entry.startedAt).toLocaleTimeString()}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {entry.status === 'completed' && (
                          <button
                            onClick={() => handleFetchResults(entry.sessionId)}
                            disabled={fetchingSessionId === entry.sessionId}
                            className="px-2 py-1 text-xs bg-[var(--color-success)]/20 text-[var(--color-success)] rounded hover:bg-[var(--color-success)]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {fetchingSessionId === entry.sessionId ? 'Fetching...' : 'Fetch Results'}
                          </button>
                        )}
                        {['processing', 'analyzing', 'uploading'].includes(entry.status) && (
                          <>
                            <button
                              onClick={() => window.location.reload()}
                              className="px-2 py-1 text-xs bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded hover:bg-[var(--bg-secondary)] transition-colors"
                            >
                              Check Progress
                            </button>
                            <button
                              onClick={cancelAnalysis}
                              className="px-2 py-1 text-xs bg-[var(--color-error)]/10 text-[var(--color-error)] rounded hover:bg-[var(--color-error)]/20 transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analysis History (PRO only) */}
      {isPro && analysisHistory.length > 0 && (
        <div className="glass rounded-xl p-6 animate-slide-up delay-275">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#ffdc18]" />
              <h2 className="text-lg font-semibold">Analysis History</h2>
              <span className="px-2 py-0.5 text-xs font-medium bg-[#ffdc18]/20 text-[#ffdc18] rounded">PRO</span>
            </div>
            {analysisHistory.length > 1 && (
              <button
                onClick={() => setShowAnalysisHistory(!showAnalysisHistory)}
                className="flex items-center gap-1 text-sm text-[#ffdc18] hover:underline"
              >
                {showAnalysisHistory ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {analysisHistory.length - 1} Older
                  </>
                )}
              </button>
            )}
          </div>
          <p className="text-sm text-white/70 mb-4">
            View and manage your past analyses. Deleted analyses are permanently removed after 30 days.
          </p>

          {/* History Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bg-tertiary)]">
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Date</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Type</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Posts</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Total Tokens</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">AI Cost</th>
                  <th className="text-left py-2 px-3 text-white/70 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(showAnalysisHistory ? analysisHistory : analysisHistory.slice(0, 1)).map((analysis, index) => {
                  const cs = analysis.costSummary ?? null;
                  const tokenDisplay = cs?.totalTokens != null
                    ? cs.totalTokens.toLocaleString('en-US')
                    : '—';
                  const costDisplay = cs?.estimatedCostUsd != null
                    ? '$' + cs.estimatedCostUsd.toFixed(2)
                    : '—';

                  return (
                    <tr 
                      key={analysis.id} 
                      className={`border-b border-[var(--bg-tertiary)]/50 ${index > 0 ? 'opacity-75' : ''}`}
                    >
                      <td className="py-3 px-3">
                        {new Date(analysis.createdAt).toLocaleDateString()} {new Date(analysis.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              analysis.analysisType === 'Incremental' 
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {analysis.analysisType || 'Baseline'}
                            </span>
                            {index === 0 && latestResults?.id === analysis.id && (
                              <span className="px-2 py-0.5 text-xs bg-[var(--color-success)]/20 text-[var(--color-success)] rounded">
                                Latest
                              </span>
                            )}
                          </div>
                          {/* Show incremental stats if available */}
                          {analysis.analysisType === 'Incremental' && (analysis.postsNew || analysis.postsChanged) && (
                            <div className="text-xs text-white/50 mt-1">
                              {analysis.postsNew ? `+${analysis.postsNew} new` : ''}
                              {analysis.postsNew && analysis.postsChanged ? ', ' : ''}
                              {analysis.postsChanged ? `~${analysis.postsChanged} changed` : ''}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-col">
                          <span>{analysis.postsAnalyzed} analyzed</span>
                          {analysis.postsUnchanged ? (
                            <span className="text-xs text-white/50">{analysis.postsUnchanged} unchanged</span>
                          ) : null}
                        </div>
                      </td>
                      {/* Total Tokens */}
                      <td className="py-3 px-3 tabular-nums text-white/80">
                        {tokenDisplay}
                      </td>
                      {/* AI Cost — with tooltip showing input/output breakdown */}
                      <td className="py-3 px-3">
                        {cs ? (
                          <span
                            className="group relative inline-flex items-center gap-1 cursor-default"
                            title={[
                              cs.totalInputTokens != null  ? `Input:  ${cs.totalInputTokens.toLocaleString('en-US')} tokens` : null,
                              cs.totalOutputTokens != null ? `Output: ${cs.totalOutputTokens.toLocaleString('en-US')} tokens` : null,
                              cs.totalAiCalls != null      ? `API calls: ${cs.totalAiCalls}` : null,
                            ].filter(Boolean).join('\n') || undefined}
                          >
                            <span className="tabular-nums font-medium text-[#ffdc18]">{costDisplay}</span>
                            {(cs.totalInputTokens != null || cs.totalAiCalls != null) && (
                              <Info className="w-3 h-3 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0" />
                            )}
                          </span>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {index === 0 && latestResults?.id === analysis.id ? (
                          <span className="px-2 py-1 text-xs bg-[var(--bg-tertiary)] text-white/70 rounded">
                            Viewing
                          </span>
                        ) : (
                          <button className="px-2 py-1 text-xs bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded hover:bg-[var(--bg-secondary)] transition-colors">
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Latest Results */}
      {hasResults && latestResults && (
        <div id="results" className="glass rounded-xl overflow-hidden animate-slide-up delay-300">
          {/* Header */}
          <div className="p-4 border-b border-[var(--bg-tertiary)]">
            <h2 className="text-lg font-semibold">Latest Results</h2>
          </div>

          {/* Executive Summary Accordion */}
          {latestResults.executiveSummary && (
            <div className="border-b border-[var(--bg-tertiary)]">
              <div 
                className="p-4 bg-[var(--bg-secondary)] flex items-center justify-between cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors"
                onClick={() => setExpandedSummary(!expandedSummary)}
              >
                <h3 className="font-semibold flex items-center gap-2">
                  📊 Executive Summary
                </h3>
                <span className="text-white/70">
                  {expandedSummary ? '▼' : '▲'}
                </span>
              </div>
              
              {expandedSummary && (
                <div className="p-6 bg-white/5">
                  <MarkdownContent content={latestResults.executiveSummary} />
                </div>
              )}
            </div>
          )}

          {/* Content Health Summary Accordion */}
          {latestResults.contentHealthSummary && (
            <div className="border-b border-[var(--bg-tertiary)]">
              <div 
                className="p-4 bg-[var(--bg-secondary)] flex items-center justify-between cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors"
                onClick={() => setContentHealthExpanded(!contentHealthExpanded)}
              >
                <h3 className="font-semibold flex items-center gap-2">
                  🏥 Content Health Summary
                </h3>
                <div className="flex items-center gap-3">
                  {latestResults.contentHealthSummary.scoring_insights && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowScoringModal(true);
                      }}
                      className="px-3 py-1 text-xs border border-[#ffdc18] text-[#ffdc18] rounded hover:bg-[#ffdc18]/10 transition-colors flex items-center gap-1"
                    >
                      💡 How Scoring Works
                    </button>
                  )}
                  <span className="text-white/70">
                    {contentHealthExpanded ? '▼' : '▲'}
                  </span>
                </div>
              </div>
              
              {contentHealthExpanded && (
                <div className="p-6">
                  {/* Dynamic Headline Banner */}
                  {latestResults.contentHealthSummary.headline && (
                    <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white">
                      <p className="text-lg font-medium flex items-center gap-2">
                        🔗 {latestResults.contentHealthSummary.headline}
                      </p>
                    </div>
                  )}

                  {/* Status Summary / Dynamic Narrative - styled like Executive Summary */}
                  {(latestResults.contentHealthSummary.status_summary || latestResults.contentHealthSummary.dynamicNarrative) && (
                    <div className="mb-6">
                      <MarkdownContent 
                        content={latestResults.contentHealthSummary.status_summary || latestResults.contentHealthSummary.dynamicNarrative || ''} 
                      />
                    </div>
                  )}

                  {/* Tier Distribution Visualization */}
                  {(() => {
                    const tierData = latestResults.contentHealthSummary?.tierDistribution || 
                                     latestResults.contentHealthSummary?.tier_distribution;
                    if (!tierData) return null;
                    
                    return (
                    <div className="space-y-4 mb-6">
                      <h4 className="font-medium flex items-center gap-2">
                        📊 Connectivity Tier Distribution
                      </h4>
                      
                      <div className="grid grid-cols-4 gap-3">
                        {/* Content Hubs - FIRST (best) */}
                        <button
                          type="button"
                          disabled={!(tierData.content_hubs || tierData.contentHubs)}
                          onClick={() => setActiveTierModal('Content Hub')}
                          className="p-4 bg-[var(--bg-tertiary)] border-l-4 border-l-[#f59e0b] rounded-r-lg text-left transition-colors enabled:hover:bg-[var(--bg-secondary)] enabled:cursor-pointer disabled:cursor-default disabled:opacity-70"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🌟</span>
                            <span className="font-semibold text-sm">Content Hubs</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold">
                              {tierData.content_hubs || tierData.contentHubs || 0}
                            </span>
                            {(tierData.content_hubs_percentage ?? tierData.contentHubsPercentage) !== undefined && (
                              <span className="text-sm text-white/70">
                                ({tierData.content_hubs_percentage ?? tierData.contentHubsPercentage}%)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-white/70 mt-1">Highly connected content leaders</div>
                        </button>
                        
                        {/* Well Connected */}
                        <button
                          type="button"
                          disabled={!(tierData.well_connected || tierData.wellConnected)}
                          onClick={() => setActiveTierModal('Well Connected')}
                          className="p-4 bg-[var(--bg-tertiary)] border-l-4 border-l-[#22c55e] rounded-r-lg text-left transition-colors enabled:hover:bg-[var(--bg-secondary)] enabled:cursor-pointer disabled:cursor-default disabled:opacity-70"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">✨</span>
                            <span className="font-semibold text-sm">Well Connected</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold">
                              {tierData.well_connected || tierData.wellConnected || 0}
                            </span>
                            {(tierData.well_connected_percentage ?? tierData.wellConnectedPercentage) !== undefined && (
                              <span className="text-sm text-white/70">
                                ({tierData.well_connected_percentage ?? tierData.wellConnectedPercentage}%)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-white/70 mt-1">Strong connectivity, good relationships</div>
                        </button>
                        
                        {/* Emerging Connections */}
                        <button
                          type="button"
                          disabled={!(tierData.emerging_connections || tierData.emergingConnections)}
                          onClick={() => setActiveTierModal('Emerging Connections')}
                          className="p-4 bg-[var(--bg-tertiary)] border-l-4 border-l-[#f59e0b] rounded-r-lg text-left transition-colors enabled:hover:bg-[var(--bg-secondary)] enabled:cursor-pointer disabled:cursor-default disabled:opacity-70"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📈</span>
                            <span className="font-semibold text-sm">Emerging Connections</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold">
                              {tierData.emerging_connections || tierData.emergingConnections || 0}
                            </span>
                            {(tierData.emerging_connections_percentage ?? tierData.emergingConnectionsPercentage) !== undefined && (
                              <span className="text-sm text-white/70">
                                ({tierData.emerging_connections_percentage ?? tierData.emergingConnectionsPercentage}%)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-white/70 mt-1">Building connections, growing</div>
                        </button>

                        {/* Isolated - LAST (needs attention) */}
                        <button
                          type="button"
                          disabled={!tierData.isolated}
                          onClick={() => setActiveTierModal('Isolated')}
                          className="p-4 bg-[var(--bg-tertiary)] border-l-4 border-l-[#ef4444] rounded-r-lg text-left transition-colors enabled:hover:bg-[var(--bg-secondary)] enabled:cursor-pointer disabled:cursor-default disabled:opacity-70"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📍</span>
                            <span className="font-semibold text-sm">Isolated</span>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold">
                              {tierData.isolated || 0}
                            </span>
                            {(tierData.isolated_percentage ?? tierData.isolatedPercentage) !== undefined && (
                              <span className="text-sm text-white/70">
                                ({tierData.isolated_percentage ?? tierData.isolatedPercentage}%)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-white/70 mt-1">Limited connections, needs attention</div>
                        </button>
                      </div>
                      <div className="text-xs text-white/50 -mt-1">Click a tier to view its posts</div>

                      {/* Progress Bar Visualization */}
                      <div className="mt-4">
                        <div className="flex h-3 rounded-full overflow-hidden bg-[var(--bg-secondary)]">
                          {/* Content Hubs first */}
                          {(tierData.content_hubs_percentage ?? tierData.contentHubsPercentage ?? 0) > 0 && (
                            <div 
                              className="bg-[#8b5cf6]" 
                              style={{ width: `${tierData.content_hubs_percentage ?? tierData.contentHubsPercentage ?? 0}%` }}
                              title={`Content Hubs: ${tierData.content_hubs_percentage ?? tierData.contentHubsPercentage ?? 0}%`}
                            />
                          )}
                          {(tierData.well_connected_percentage ?? tierData.wellConnectedPercentage ?? 0) > 0 && (
                            <div 
                              className="bg-[#22c55e]" 
                              style={{ width: `${tierData.well_connected_percentage ?? tierData.wellConnectedPercentage ?? 0}%` }}
                              title={`Well Connected: ${tierData.well_connected_percentage ?? tierData.wellConnectedPercentage ?? 0}%`}
                            />
                          )}
                          {(tierData.emerging_connections_percentage ?? tierData.emergingConnectionsPercentage ?? 0) > 0 && (
                            <div 
                              className="bg-[#f59e0b]" 
                              style={{ width: `${tierData.emerging_connections_percentage ?? tierData.emergingConnectionsPercentage ?? 0}%` }}
                              title={`Emerging: ${tierData.emerging_connections_percentage ?? tierData.emergingConnectionsPercentage ?? 0}%`}
                            />
                          )}
                          {(tierData.isolated_percentage ?? tierData.isolatedPercentage ?? 0) > 0 && (
                            <div 
                              className="bg-[#ef4444]" 
                              style={{ width: `${tierData.isolated_percentage ?? tierData.isolatedPercentage ?? 0}%` }}
                              title={`Isolated: ${tierData.isolated_percentage ?? tierData.isolatedPercentage ?? 0}%`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })()}

                  {/* Strategic Recommendations (PRO only) */}
                  {isPro && latestResults.contentHealthSummary.recommendations && latestResults.contentHealthSummary.recommendations.length > 0 && (
                    <div className="mt-6 p-4 bg-[var(--bg-tertiary)] rounded-lg border-l-4 border-[#ffdc18]">
                      <h4 className="font-semibold flex items-center gap-2 mb-4">
                        🎯 COMPLETE THESE ACTIONS FIRST
                        <span className="px-2 py-0.5 text-xs font-medium bg-[#ffdc18]/20 text-[#ffdc18] rounded">PRO</span>
                      </h4>
                      
                      <div className="space-y-4">
                        {/* Sort recommendations: Hub recommendations first, then by priority */}
                        {[...latestResults.contentHealthSummary.recommendations]
                          .sort((a, b) => {
                            // Hub recommendations come first (check action text)
                            const aIsHub = (a.action || '').toLowerCase().includes('hub');
                            const bIsHub = (b.action || '').toLowerCase().includes('hub');
                            if (aIsHub && !bIsHub) return -1;
                            if (!aIsHub && bIsHub) return 1;
                            
                            // Then sort by priority: HIGH > MEDIUM > LOW
                            const priorityOrder: Record<string, number> = { 'HIGH': 0, 'MEDIUM': 1, 'LOW': 2 };
                            const aPriority = (a.priority || 'MEDIUM').toUpperCase();
                            const bPriority = (b.priority || 'MEDIUM').toUpperCase();
                            return (priorityOrder[aPriority] ?? 1) - (priorityOrder[bPriority] ?? 1);
                          })
                          .map((rec, index) => {
                            const recommendationId = generateRecommendationId(rec.action);
                            return (
                              <StrategicRecommendationCard 
                                key={recommendationId} 
                                recommendation={rec} 
                                index={index}
                                platformId={currentSite?.platformId}
                                recommendationId={recommendationId}
                                completedPostIds={getCompletedPostsForRecommendation(recommendationId)}
                                onToggleComplete={(postId) => handleTogglePostComplete(recommendationId, postId)}
                                isLoadingCompleted={completedActionsLoading}
                                pendingCountsByPost={pendingCountsByPost}
                              />
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Upgrade notice for non-PRO users */}
                  {!isPro && latestResults.contentHealthSummary.recommendations && latestResults.contentHealthSummary.recommendations.length > 0 && (
                    <div className="mt-6 p-4 bg-[var(--bg-tertiary)] rounded-lg border-l-4 border-[#ffdc18]">
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        🎯 COMPLETE THESE ACTIONS FIRST
                        <span className="px-2 py-0.5 text-xs font-medium bg-[#ffdc18]/20 text-[#ffdc18] rounded">PRO</span>
                      </h4>
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <Lock className="w-4 h-4" />
                        <p>Upgrade to PRO to unlock strategic recommendations with detailed action items and progress tracking.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Strategic Category Recommendations */}
          {latestResults.categoryRecommendations && (
            <StrategicCategoryRecommendations 
              data={latestResults.categoryRecommendations}
              siteId={currentSite?.id}
              analysisId={latestResults.id}
              cmsType={currentSite?.cmsType}
              completedActions={completedActions}
              onActionComplete={(rec) => setCompletedActions(prev => {
                const id = generateRecommendationId(rec);
                const exists = prev.some(a => a.recommendationId === id && a.postId === 0);
                if (exists) return prev;
                return [...prev, { recommendationId: id, postId: 0, completedAt: new Date().toISOString() }];
              })}
            />
          )}

          {/* Hub Posts Overview - PRO only */}
          {isPro && latestResults.hubPostsOverview && latestResults.hubPostsOverview.length > 0 && (
            <HubPostsOverviewSection 
              hubPosts={latestResults.hubPostsOverview}
              platformId={currentSite?.platformId}
            />
          )}

          {/* Quick Navigation */}
          <div className="mt-6 pt-6 border-t border-[var(--bg-tertiary)]">
            <h3 className="font-semibold mb-4">View Detailed Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Link Recommendations */}
              {linkStats && linkStats.total > 0 && (
                <Link
                  to="/links"
                  className="flex items-center justify-between p-4 bg-[#f472b6]/10 border-l-4 border-[#f472b6] rounded-r-lg hover:bg-[#f472b6]/20 transition-colors"
                >
                  <div>
                    <p className="font-medium">🔗 Link Recommendations</p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {linkStats.total} recommendations • {linkStats.pending} pending
                    </p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#f472b6]" />
                </Link>
              )}

              {/* Content Analysis */}
              <Link
                to="/content-analysis"
                className="flex items-center justify-between p-4 bg-[#60a5fa]/10 border-l-4 border-[#60a5fa] rounded-r-lg hover:bg-[#60a5fa]/20 transition-colors"
              >
                <div>
                  <p className="font-medium">📊 Content Analysis</p>
                  <p className="text-sm text-[var(--text-secondary)]">
                    View content islands, bridges, and standalone posts in detail.
                  </p>
                </div>
                <ExternalLink className="w-5 h-5 text-[#60a5fa]" />
              </Link>

              {/* Content Gaps */}
              <Link
                to="/content-gaps"
                className="flex items-center justify-between p-4 bg-[#4ade80]/10 border-l-4 border-[#4ade80] rounded-r-lg hover:bg-[#4ade80]/20 transition-colors"
              >
                <div>
                  <p className="font-medium">📝 Content Gaps</p>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Discover content opportunities and gaps in your coverage.
                  </p>
                </div>
                <ExternalLink className="w-5 h-5 text-[#4ade80]" />
              </Link>

              {/* Posts Needing Attention */}
              <Link
                to="/posts-attention"
                className="flex items-center justify-between p-4 bg-[#fbbf24]/10 border-l-4 border-[#fbbf24] rounded-r-lg hover:bg-[#fbbf24]/20 transition-colors"
              >
                <div>
                  <p className="font-medium">⚠️ Posts Needing Attention</p>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Review posts that need action, updates, or improvements.
                  </p>
                </div>
                <ExternalLink className="w-5 h-5 text-[#fbbf24]" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Scoring Insights Modal */}
      {showScoringModal && latestResults?.contentHealthSummary?.scoring_insights && (
        <ScoringInsightsModal 
          insights={latestResults.contentHealthSummary.scoring_insights}
          onClose={() => setShowScoringModal(false)}
        />
      )}

      {/* Connectivity Tier Drill-Down Modal */}
      {activeTierModal && (
        <ConnectivityTierModal
          tier={activeTierModal}
          postsData={tierPostsData}
          isLoading={tierPostsLoading}
          error={tierPostsError}
          siteId={currentSite?.id}
          platformId={currentSite?.platformId}
          isHtmlClient={currentSite?.cmsType === 'html' || currentSite?.platform === 'html'}
          onClose={() => setActiveTierModal(null)}
        />
      )}
    </div>
  );
}

// Scoring Insights Modal Component
interface ScoringInsight {
  general_tips?: string[];
  badge_criteria?: {
    badge_name: string;
    badge_type: string;
    badge_description: string;
    general_requirements: string;
  }[];
  tier_thresholds?: {
    content_hub: number;
    well_connected: number;
    emerging_connections: number;
  };
}

function ScoringInsightsModal({ insights, onClose }: { insights: ScoringInsight; onClose: () => void }) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[var(--bg-primary)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border border-[var(--bg-tertiary)]">
        {/* Header */}
        <div className="p-4 border-b border-[var(--bg-tertiary)] flex items-center justify-between bg-gradient-to-r from-[#3b82f6]/20 to-[#8b5cf6]/20">
          <h2 className="text-xl font-bold flex items-center gap-2">
            💡 How Connectivity Scoring Works
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] space-y-6">
          {/* General Tips */}
          {insights.general_tips && insights.general_tips.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                📚 Scoring Principles
              </h3>
              <ul className="space-y-2">
                {insights.general_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                    <span className="text-[#3b82f6] mt-1">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Tier Thresholds */}
          {insights.tier_thresholds && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                🎯 Connectivity Tiers
              </h3>
              <div className="space-y-3">
                {/* Content Hub */}
                <div className="flex items-center gap-3 p-3 bg-[#8b5cf6]/10 rounded-lg border border-[#8b5cf6]/30">
                  <span className="text-2xl">🏆</span>
                  <div className="flex-1">
                    <div className="font-medium text-[#8b5cf6]">Content Hub</div>
                    <div className="text-xs text-white/70">{insights.tier_thresholds.content_hub}+ points</div>
                  </div>
                  <div className="w-20 h-2 bg-[#8b5cf6] rounded-full" />
                </div>
                
                {/* Well Connected */}
                <div className="flex items-center gap-3 p-3 bg-[#22c55e]/10 rounded-lg border border-[#22c55e]/30">
                  <span className="text-2xl">🔗</span>
                  <div className="flex-1">
                    <div className="font-medium text-[#22c55e]">Well Connected</div>
                    <div className="text-xs text-white/70">{insights.tier_thresholds.well_connected}-{insights.tier_thresholds.content_hub - 1} points</div>
                  </div>
                  <div className="w-16 h-2 bg-[#22c55e] rounded-full" />
                </div>
                
                {/* Emerging Connections */}
                <div className="flex items-center gap-3 p-3 bg-[#f59e0b]/10 rounded-lg border border-[#f59e0b]/30">
                  <span className="text-2xl">🌱</span>
                  <div className="flex-1">
                    <div className="font-medium text-[#f59e0b]">Emerging Connections</div>
                    <div className="text-xs text-white/70">{insights.tier_thresholds.emerging_connections}-{insights.tier_thresholds.well_connected - 1} points</div>
                  </div>
                  <div className="w-12 h-2 bg-[#f59e0b] rounded-full" />
                </div>
                
                {/* Isolated */}
                <div className="flex items-center gap-3 p-3 bg-[#ef4444]/10 rounded-lg border border-[#ef4444]/30">
                  <span className="text-2xl">📍</span>
                  <div className="flex-1">
                    <div className="font-medium text-[#ef4444]">Isolated</div>
                    <div className="text-xs text-white/70">0-{insights.tier_thresholds.emerging_connections - 1} points</div>
                  </div>
                  <div className="w-8 h-2 bg-[#ef4444] rounded-full" />
                </div>
              </div>
            </div>
          )}
          
          {/* Badge Criteria */}
          {insights.badge_criteria && insights.badge_criteria.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                🏅 Available Badges
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {insights.badge_criteria.map((badge, i) => (
                  <div key={i} className="p-3 bg-[var(--bg-tertiary)] rounded-lg">
                    <div className="font-medium text-sm mb-1">{badge.badge_name}</div>
                    <div className="text-xs text-white/70 mb-2">{badge.badge_description}</div>
                    <div className="text-xs text-white/80">
                      <strong className="text-[#ffdc18]">How to earn:</strong> {badge.general_requirements}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Tier metadata for the drill-down modal — icon/color/description keyed by the
// exact `tier` string values the backend returns (see analysis.js: "Tiers:
// 'Content Hub', 'Well Connected', 'Emerging Connections', 'Isolated'").
const TIER_MODAL_META: Record<TierName, { icon: string; color: string; description: string }> = {
  'Content Hub': { icon: '🌟', color: '#f59e0b', description: 'Highly connected content leaders' },
  'Well Connected': { icon: '✨', color: '#22c55e', description: 'Strong connectivity, good relationships' },
  'Emerging Connections': { icon: '📈', color: '#f59e0b', description: 'Building connections, growing' },
  'Isolated': { icon: '📍', color: '#ef4444', description: 'Limited connections, needs attention' },
};

interface ConnectivityTierModalProps {
  tier: TierName;
  postsData: Record<string, PostConnectivityScore> | null;
  isLoading: boolean;
  error: string | null;
  siteId?: number;
  platformId?: string;
  isHtmlClient: boolean;
  onClose: () => void;
}

function ConnectivityTierModal({ tier, postsData, isLoading, error, siteId, platformId, isHtmlClient, onClose }: ConnectivityTierModalProps) {
  const [search, setSearch] = useState('');
  const meta = TIER_MODAL_META[tier];

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const posts = useMemo(() => {
    if (!postsData) return [];
    const inTier = Object.values(postsData).filter(p => p.tier === tier);
    // Secondary sort by category so same-category posts naturally cluster,
    // primary sort keeps strongest connectivity within the tier first.
    inTier.sort((a, b) => {
      const catCompare = (a.postTag || '').localeCompare(b.postTag || '');
      if (catCompare !== 0) return catCompare;
      return (b.connectivityScore || 0) - (a.connectivityScore || 0);
    });
    if (!search.trim()) return inTier;
    const q = search.trim().toLowerCase();
    return inTier.filter(p =>
      (p.postTitle || '').toLowerCase().includes(q) ||
      (p.postTag || '').toLowerCase().includes(q)
    );
  }, [postsData, tier, search]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[var(--bg-primary)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border border-[var(--bg-tertiary)] flex flex-col">
        {/* Header */}
        <div
          className="p-4 border-b border-[var(--bg-tertiary)] flex items-center justify-between"
          style={{ background: `linear-gradient(to right, ${meta.color}33, ${meta.color}11)` }}
        >
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              {meta.icon} {tier} {!isLoading && !error && `(${posts.length})`}
            </h2>
            <p className="text-xs text-white/60 mt-0.5">{meta.description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        {!isLoading && !error && postsData && Object.values(postsData).some(p => p.tier === tier) && (
          <div className="p-3 border-b border-[var(--bg-tertiary)]">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title or category..."
              className="w-full px-3 py-2 text-sm rounded-lg bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
            />
          </div>
        )}

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-2">
          {isLoading && (
            <div className="flex items-center justify-center py-10 text-white/70">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading posts...
            </div>
          )}

          {!isLoading && error && (
            <div className="text-center py-10">
              <p className="text-sm text-red-400 mb-3">{error}</p>
            </div>
          )}

          {!isLoading && !error && posts.length === 0 && (
            <div className="text-center py-10 text-sm text-white/60">
              {search.trim() ? 'No posts match your search.' : 'No detailed post data available for this analysis.'}
            </div>
          )}

          {!isLoading && !error && posts.map(post => {
            // HubSpot: open Draft Post Links → "Search by Title/URL" with this post
            // pre-selected. HTML clients have no Draft Post Links page, so link to the live post.
            const linksUrl = !isHtmlClient && siteId
              ? `/draft-links?${new URLSearchParams({
                  tab: 'search',
                  siteId: String(siteId),
                  postId: String(post.postId),
                  q: post.postTitle || '',
                }).toString()}`
              : null;
            const viewUrl = isHtmlClient && post.postUrl ? post.postUrl : null;
            // Link recommendations need a topical tag, so an uncategorized post
            // must get one in the HubSpot editor first.
            const isUncategorized = !isHtmlClient && post.postTag?.toLowerCase() === 'uncategorized';
            const editorUrl = isUncategorized && platformId
              ? `https://app.hubspot.com/blog/${platformId}/editor/${post.postId}/content`
              : null;
            const tagLabel = post.postTag || (isHtmlClient ? 'Uncategorized' : 'Category not recorded');

            return (
              <div
                key={post.postId}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[var(--bg-tertiary)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{post.postTitle || `Post #${post.postId}`}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${
                        isUncategorized ? 'bg-amber-500/15 text-amber-300' : 'bg-white/10 text-white/70'
                      }`}
                    >
                      <Tag className="w-3 h-3" />
                      {tagLabel}
                    </span>
                    {isUncategorized && (
                      <span className="text-xs text-white/50">Add a category before requesting links</span>
                    )}
                  </div>
                </div>
                {editorUrl && (
                  <a
                    href={editorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open this post in the HubSpot editor to assign a category (tag) in its settings"
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-400 text-amber-300 hover:bg-amber-400/10 transition-colors"
                  >
                    Add Category <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {(linksUrl || viewUrl) && (
                  <a
                    href={linksUrl || viewUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={linksUrl ? 'Open Draft Post Links for this post in a new tab' : undefined}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#3b82f6] text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors"
                  >
                    {linksUrl ? 'Get Links' : 'View'} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Strategic Recommendation Card Component
interface StrategicRecommendationCardProps {
  recommendation: {
    action: string;
    impact: string;
    priority: 'high' | 'medium' | 'low';
    posts_affected?: number;
    details?: {
      affected_posts?: {
        post_id: number;
        post_title: string;
        current_status: string;
        estimated_points?: number;
        will_become?: string;
      }[];
      effort_estimate?: 'quick' | 'moderate' | 'extended';
      estimated_total_impact?: string;
      badge_progress?: string[];
    };
  };
  index?: number;
  platformId?: string;
  // New props for persistence
  recommendationId: string;
  completedPostIds: number[];
  onToggleComplete: (postId: number) => void;
  isLoadingCompleted?: boolean;
  // Pending link counts by post ID
  pendingCountsByPost: Record<string, number>;
}

function StrategicRecommendationCard({ 
  recommendation: rec, 
  platformId,
  completedPostIds,
  onToggleComplete,
  isLoadingCompleted = false,
  pendingCountsByPost
}: StrategicRecommendationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [togglingPostId, setTogglingPostId] = useState<number | null>(null);
  
  const hasDetails = rec.details && rec.details.affected_posts && rec.details.affected_posts.length > 0;
  const totalPosts = rec.details?.affected_posts?.length || 0;
  const completedCount = completedPostIds.length;
  
  // Check if this is an external citation recommendation (no link recommendations for these)
  const isExternalCitation = rec.action.toLowerCase().includes('external citation') || 
                             rec.action.toLowerCase().includes('authoritative');
  
  const effortLabels: Record<string, { emoji: string; label: string; color: string }> = {
    quick: { emoji: '⚡', label: 'Quick (~5-15 min)', color: 'text-[#22c55e]' },
    moderate: { emoji: '⏱️', label: 'Moderate (~15-30 min)', color: 'text-[#f59e0b]' },
    extended: { emoji: '📅', label: 'Extended (~30+ min)', color: 'text-[#ef4444]' }
  };
  
  const priorityColors: Record<string, string> = {
    high: 'bg-[#ef4444] text-white',
    medium: 'bg-[#f59e0b] text-white',
    low: 'bg-[#3b82f6] text-white'
  };
  
  // Handle toggle with loading state
  const handleToggle = async (postId: number) => {
    setTogglingPostId(postId);
    try {
      await onToggleComplete(postId);
    } finally {
      setTogglingPostId(null);
    }
  };
  
  return (
    <div className="bg-white/5 border border-[var(--bg-tertiary)] rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${priorityColors[rec.priority] || priorityColors.medium}`}>
            {rec.priority.toUpperCase()}
          </span>
          <div className="flex-1">
            <p className="font-semibold">{rec.action}</p>
            {rec.impact && (
              <p className="text-sm text-[var(--text-secondary)] mt-1">{rec.impact}</p>
            )}
          </div>
        </div>
        
        {/* Metadata Row */}
        {hasDetails && (
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            {rec.details?.affected_posts && (
              <span className="flex items-center gap-1">
                📋 {totalPosts} post{totalPosts !== 1 ? 's' : ''}
              </span>
            )}
            {rec.details?.effort_estimate && effortLabels[rec.details.effort_estimate] && (
              <span className={`flex items-center gap-1 ${effortLabels[rec.details.effort_estimate].color}`}>
                {effortLabels[rec.details.effort_estimate].emoji} {effortLabels[rec.details.effort_estimate].label}
              </span>
            )}
            {rec.details?.estimated_total_impact && (
              <span className="flex items-center gap-1">
                💡 {rec.details.estimated_total_impact}
              </span>
            )}
          </div>
        )}
        
        {/* Badge Progress */}
        {rec.details?.badge_progress && rec.details.badge_progress.length > 0 && (
          <div className="mt-3 p-2 bg-[var(--bg-tertiary)] rounded">
            <p className="text-sm font-medium">🏅 Badge Progress:</p>
            <ul className="text-sm text-[var(--text-secondary)] mt-1 ml-4 list-disc">
              {rec.details.badge_progress.map((progress, i) => (
                <li key={i}>{progress}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Completion Progress */}
        {completedCount > 0 && (
          <div className={`mt-3 text-sm ${completedCount === totalPosts ? 'text-[#22c55e]' : 'text-[var(--text-secondary)]'}`}>
            {completedCount === totalPosts && '✅ '}
            {completedCount} of {totalPosts} posts completed
          </div>
        )}
        
        {/* Toggle Button */}
        {hasDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 w-full py-2 px-4 bg-[var(--bg-tertiary)] hover:bg-[#ffdc18]/10 border border-[#ffdc18]/40 hover:border-[#ffdc18]/70 rounded text-sm font-medium text-[#ffdc18] transition-colors flex items-center justify-center gap-1"
          >
            {expanded ? '▲ Hide Affected Posts' : '▼ Show Affected Posts'}
          </button>
        )}
      </div>
      
      {/* Expanded Posts List */}
      {expanded && hasDetails && (
        <div className="border-t border-[var(--bg-tertiary)] p-4 space-y-3">
          {isLoadingCompleted && (
            <div className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading completion status...
            </div>
          )}
          {rec.details?.affected_posts?.map((post) => {
            const isCompleted = completedPostIds.includes(post.post_id);
            const isToggling = togglingPostId === post.post_id;
            
            return (
              <div 
                key={post.post_id}
                className={`p-3 rounded-lg border transition-all ${
                  isCompleted 
                    ? 'bg-[#22c55e]/10 border-[#22c55e]/30 opacity-70' 
                    : 'bg-[var(--bg-tertiary)] border-[var(--bg-tertiary)]'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => handleToggle(post.post_id)}
                    disabled={isToggling || isLoadingCompleted}
                    className={`text-2xl leading-none mt-0.5 flex-shrink-0 ${isToggling ? 'opacity-50' : ''}`}
                    title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {isToggling ? '⏳' : isCompleted ? '✅' : '☐'}
                  </button>
                  
                  {/* Post Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {platformId ? (
                        <a
                          href={`https://app.hubspot.com/blog/${platformId}/editor/${post.post_id}/content`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`font-medium hover:underline ${isCompleted ? 'text-[#22c55e]' : 'text-[#ffdc18]'}`}
                        >
                          {post.post_title} ({post.post_id}) ↗
                        </a>
                      ) : (
                        <span className={`font-medium ${isCompleted ? 'text-[#22c55e]' : ''}`}>
                          {post.post_title} ({post.post_id})
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-xs px-2 py-0.5 bg-[#22c55e]/20 text-[#22c55e] rounded font-medium">
                          DONE
                        </span>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div className={`text-sm mt-1 ${isCompleted ? 'text-[#22c55e]/80' : 'text-[var(--text-secondary)]'}`}>
                      {post.current_status}
                      {post.will_become && !isCompleted && (
                        <>
                          {' → '}
                          <span className="font-medium text-[#22c55e]">{post.will_become}</span>
                        </>
                      )}
                    </div>
                    
                    {/* Points */}
                    {post.estimated_points !== undefined && !isCompleted && (
                      <div className="text-sm mt-1 text-[#22c55e]">
                        +{post.estimated_points.toFixed(1)} points
                      </div>
                    )}
                  </div>
                </div>
                
                {/* View Recommendations Button - Skip for external citation recommendations and posts with no pending links */}
                {!isCompleted && !isExternalCitation && (pendingCountsByPost[String(post.post_id)] || 0) > 0 && (
                  <div className="mt-3 ml-9">
                    <Link
                      to={`/links?postId=${post.post_id}&postTitle=${encodeURIComponent(post.post_title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#ffdc18] text-[#ffdc18] hover:bg-[#ffdc18]/10 rounded transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      View {pendingCountsByPost[String(post.post_id)]} Recommendation{pendingCountsByPost[String(post.post_id)] !== 1 ? 's' : ''}
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Strategic Category Recommendations Component
interface StrategicCategoryRecommendationsProps {
  data: CategoryRecommendations;
  siteId?: number;
  analysisId?: number;
  cmsType?: string;
  completedActions?: { recommendationId: string; postId: number; completedAt: string }[];
  onActionComplete?: (recLabel: string) => void;
}

type TagDispositionAction = 'keep' | 'mark_structural' | 'remove';
interface TagDisposition { action: TagDispositionAction; tagId: string; }

interface DispositionPrompt {
  recIndex: number;
  implKey: string;
  originalCatName: string;
  newCatIndex: number;
  newTagName: string;
  postIds: number[];
}

function StrategicCategoryRecommendations({
  data,
  siteId,
  analysisId,
  cmsType,
  completedActions = [],
  onActionComplete,
}: StrategicCategoryRecommendationsProps) {
  const isHtmlSite = cmsType === 'html';
  const [expanded, setExpanded] = useState(false);
  const [expandedRationale, setExpandedRationale] = useState<Set<string>>(new Set());
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const [implementingKey, setImplementingKey] = useState<string | null>(null);
  const [manuallyApplyingKey, setManuallyApplyingKey] = useState<string | null>(null);
  const [tagDispositions, setTagDispositions] = useState<Record<string, TagDisposition>>({});
  const [dispositionPrompt, setDispositionPrompt] = useState<DispositionPrompt | null>(null);
  const [manualApplyPrompt, setManualApplyPrompt] = useState<{
    recIndex: number; implKey: string; catIndex: number; originalCat: string; newCatName: string;
  } | null>(null);
  const [manualCategoryName, setManualCategoryName] = useState('');
  const [pendingTagAction, setPendingTagAction] = useState<TagDispositionAction>('mark_structural');
  const [pendingTagId, setPendingTagId] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<HubSpotTagWithStructural[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [implementError, setImplementError] = useState<string | null>(null);

  // Load HubSpot tags once for tag-ID resolution in the disposition prompt
  useEffect(() => {
    if (isHtmlSite || !siteId) return;
    setTagsLoading(true);
    structuralTagsApi.getAll(siteId)
      .then(res => { if (res.success) setAvailableTags(res.tags); })
      .catch(() => {})
      .finally(() => setTagsLoading(false));
  }, [siteId, isHtmlSite]);

  // Normalize split_implementation → [{ implKey, impl }]
  const getSplitImpls = (si: SplitImplementation | Record<string, SplitImplementation> | undefined) => {
    if (!si) return [] as { implKey: string; impl: SplitImplementation }[];
    if ((si as SplitImplementation).new_categories !== undefined) {
      const impl = si as SplitImplementation;
      return [{ implKey: impl.original_category || 'default', impl }];
    }
    return Object.entries(si as Record<string, SplitImplementation>).map(([k, v]) => ({ implKey: k, impl: v }));
  };

  const makeCatKey = (recIndex: number, implKey: string, catIndex: number) => `${recIndex}-${implKey}-${catIndex}`;
  const makeRecId = (originalCat: string, newCatName: string) => `split:${originalCat}:${newCatName}`;

  const isNewCatDone = (originalCat: string, newCatName: string) => {
    const recId = makeRecId(originalCat, newCatName);
    return completedActions.some(a => a.recommendationId === recId);
  };

  const toggleRationale = (key: string) =>
    setExpandedRationale(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const togglePosts = (key: string) =>
    setExpandedPosts(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

  const doImplement = async (originalCat: string, newTagName: string, postIds: number[], disp: TagDisposition) => {
    if (!siteId) return;
    const recId = makeRecId(originalCat, newTagName);
    const res = await implementCategoryApi.implement(siteId, {
      mode: 'implement',
      recommendationId: recId,
      // VizzEx creates the tag itself here, so pseudo_category (AI suggestion)
      // and wordpress_category (actual tag name) are identical by construction.
      originalCategory: originalCat,
      pseudoCategory: newTagName,
      analysisId,
      newTagName,
      postIds,
      originalTagId: disp.action !== 'keep' ? disp.tagId || undefined : undefined,
      originalTagAction: disp.action,
    });
    if (!res.success) throw new Error(res.error || 'Failed to implement category');
    onActionComplete?.(recId);
  };

  const handleImplementClick = async (
    recIndex: number, implKey: string, catIndex: number,
    originalCat: string, newCatName: string, postIds: number[]
  ) => {
    if (!siteId) return;
    setImplementError(null);
    const catKey = makeCatKey(recIndex, implKey, catIndex);
    const existing = tagDispositions[originalCat];
    if (existing) {
      setImplementingKey(catKey);
      try { await doImplement(originalCat, newCatName, postIds, existing); }
      catch (e) { setImplementError(e instanceof Error ? e.message : 'Failed'); }
      finally { setImplementingKey(null); }
    } else {
      const matched = availableTags.find(t => t.name.toLowerCase() === originalCat.toLowerCase());
      setPendingTagId(matched?.id || '');
      setPendingTagAction('mark_structural');
      setDispositionPrompt({ recIndex, implKey, originalCatName: originalCat, newCatIndex: catIndex, newTagName: newCatName, postIds });
    }
  };

  const handleDispositionConfirm = async () => {
    if (!dispositionPrompt || !siteId) return;
    const { originalCatName, newTagName, postIds, recIndex, implKey, newCatIndex } = dispositionPrompt;
    const disp: TagDisposition = { action: pendingTagAction, tagId: pendingTagId };
    setTagDispositions(prev => ({ ...prev, [originalCatName]: disp }));
    setDispositionPrompt(null);
    const catKey = makeCatKey(recIndex, implKey, newCatIndex);
    setImplementingKey(catKey);
    try { await doImplement(originalCatName, newTagName, postIds, disp); }
    catch (e) { setImplementError(e instanceof Error ? e.message : 'Failed'); }
    finally { setImplementingKey(null); }
  };

  // Opens an inline prompt asking the user to confirm/edit the real category
  // name they used in the CMS — needed because wordpress_category must be the
  // ACTUAL name (which may differ from the AI's pseudoCategory suggestion if
  // they renamed it while creating it themselves).
  const handleManuallyApplied = (
    recIndex: number, implKey: string, catIndex: number,
    originalCat: string, newCatName: string
  ) => {
    setImplementError(null);
    setManualCategoryName(newCatName);
    setManualApplyPrompt({ recIndex, implKey, catIndex, originalCat, newCatName });
  };

  const handleManualApplyConfirm = async () => {
    if (!manualApplyPrompt || !siteId) return;
    const { recIndex, implKey, catIndex, originalCat, newCatName } = manualApplyPrompt;
    const wordpressCategory = manualCategoryName.trim() || newCatName;
    const catKey = makeCatKey(recIndex, implKey, catIndex);
    const recId = makeRecId(originalCat, newCatName);
    setManualApplyPrompt(null);
    setManuallyApplyingKey(catKey);
    try {
      const res = await implementCategoryApi.implement(siteId, {
        mode: 'manually_applied',
        recommendationId: recId,
        originalCategory: originalCat,
        pseudoCategory: newCatName,
        wordpressCategory,
        analysisId,
      });
      if (!res.success) throw new Error(res.error || 'Failed to record category');
      onActionComplete?.(recId);
    } catch (e) {
      setImplementError(e instanceof Error ? e.message : 'Failed to record category');
    } finally {
      setManuallyApplyingKey(null);
    }
  };

  const recommendationCount = data.specific_recommendations?.length || 0;
  const splitCount = data.specific_recommendations?.filter(r => r.action_type === 'split').length || 0;

  const getPriorityBadge = (priority?: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-[#ef4444] text-white', high: 'bg-[#f59e0b] text-white',
      medium: 'bg-[#3b82f6] text-white', low: 'bg-[#6b7280] text-white',
    };
    return colors[priority?.toLowerCase() || 'medium'] || colors.medium;
  };
  
  return (
    <div className="mt-6 glass rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="p-4 bg-[var(--bg-tertiary)] cursor-pointer flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="font-semibold flex items-center gap-2">
          📊 Strategic Category Recommendations
          {splitCount > 0 && (
            <span className="text-sm font-normal text-white/60">
              — {splitCount} Category Split{splitCount !== 1 ? 's' : ''} Required
            </span>
          )}
        </h3>
        <span className="text-white/70">{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div className="p-4 space-y-6">
          {/* Critical Action Alert */}
          {data.mandatory_size_audit?.critical_action_required === 'true' && (
            <div className="p-4 bg-[#ef4444] text-white rounded-lg">
              <h4 className="font-bold flex items-center gap-2 mb-1">🚨 Critical Action Required</h4>
              <p className="font-medium">Your content has structural issues that must be addressed before optimization can be effective.</p>
            </div>
          )}

          {/* Strategic Assessment + Recommended Strategy */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.strategic_assessment && (
              <div className="glass rounded-lg overflow-hidden">
                <div className="p-3 bg-[var(--bg-tertiary)] flex items-center gap-3">
                  <span className="text-2xl">🎯</span>
                  <div className="flex-1">
                    <div className="font-semibold">Strategic Assessment</div>
                    <div className="text-xs text-white/70">Primary Challenge &amp; Opportunity Analysis</div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded ${getPriorityBadge('critical')}`}>CRITICAL</span>
                </div>
                <div className="p-4 space-y-4">
                  {data.strategic_assessment.primary_challenge && (
                    <div className="border-l-4 border-[#ef4444] pl-3">
                      <h5 className="text-sm font-medium text-[#ef4444] mb-1">Primary Challenge</h5>
                      <p className="font-semibold capitalize">{data.strategic_assessment.primary_challenge.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                  {data.strategic_assessment.biggest_opportunity && (
                    <div className="border-l-4 border-[#22c55e] pl-3">
                      <h5 className="text-sm font-medium text-[#22c55e] mb-1">💡 Biggest Opportunity</h5>
                      <p className="text-[#22c55e]/90">{data.strategic_assessment.biggest_opportunity}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {data.recommended_strategy && (
              <div className="glass rounded-lg overflow-hidden">
                <div className="p-3 bg-[var(--bg-tertiary)] flex items-center gap-3">
                  <span className="text-2xl">📋</span>
                  <div className="flex-1">
                    <div className="font-semibold">Recommended Strategy</div>
                    <div className="text-xs text-white/70">
                      {data.recommended_strategy.strategy_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} • {data.recommended_strategy.priority_level?.replace(/\b\w/g, l => l.toUpperCase())} Priority
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-bold rounded ${getPriorityBadge(data.recommended_strategy.priority_level)}`}>
                    {(data.recommended_strategy.priority_level || 'HIGH').toUpperCase()}
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  {data.recommended_strategy.expected_impact && (
                    <div className="border-l-4 border-[#f59e0b] pl-3">
                      <h5 className="text-sm font-medium text-[#f59e0b] mb-1">Expected Impact</h5>
                      <p>{data.recommended_strategy.expected_impact}</p>
                    </div>
                  )}
                  {data.recommended_strategy.rationale && (
                    <div className="border-l-4 border-[#3b82f6] pl-3">
                      <h5 className="text-sm font-medium text-[#3b82f6] mb-1">Strategic Rationale</h5>
                      <p className="italic text-[var(--text-secondary)]">{data.recommended_strategy.rationale}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recommended Actions */}
          {recommendationCount > 0 && (
            <div className="space-y-5">
              <h4 className="font-semibold">Recommended Actions</h4>

              {data.specific_recommendations!.map((rec, recIndex) => {
                const splitImpls = getSplitImpls(rec.split_implementation as SplitImplementation | Record<string, SplitImplementation> | undefined);
                const isSplit = rec.action_type === 'split' && splitImpls.length > 0;

                if (!isSplit) {
                  const cats = Array.isArray(rec.target_categories) ? rec.target_categories.join(', ') : rec.target_categories || '';
                  return (
                    <div key={recIndex} className="glass rounded-lg overflow-hidden">
                      <div className="p-3 bg-[var(--bg-tertiary)] flex items-center gap-3">
                        <span className="text-2xl">🎯</span>
                        <div className="flex-1">
                          <div className="font-semibold capitalize">{rec.action_type}: {cats}</div>
                          <div className="text-xs text-white/70">{rec.action_priority?.toUpperCase()} Priority</div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${getPriorityBadge(rec.action_priority)}`}>
                          {(rec.action_priority || 'MEDIUM').toUpperCase()}
                        </span>
                      </div>
                      {rec.business_justification && (
                        <div className="p-3 text-sm text-[var(--text-secondary)]">{rec.business_justification}</div>
                      )}
                    </div>
                  );
                }

                // ── Rich split card ────────────────────────────────────────
                return splitImpls.map(({ implKey, impl }) => {
                  const originalCat = impl.original_category || implKey;
                  const newCats: NewCategorySpec[] = impl.new_categories || [];
                  const totalPosts = newCats.reduce((s, nc) => s + (nc.post_count ?? nc.post_ids?.length ?? 0), 0);
                  const allDone = newCats.length > 0 && newCats.every(nc => isNewCatDone(originalCat, nc.category_name));
                  const showPromptHere = dispositionPrompt?.recIndex === recIndex && dispositionPrompt?.implKey === implKey;

                  return (
                    <div key={`${recIndex}-${implKey}`} className="glass rounded-lg overflow-hidden">
                      {/* Split header */}
                      <div className="p-4 bg-[var(--bg-tertiary)] flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#f59e0b]/20 flex items-center justify-center text-xl shrink-0">✂️</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-base">
                            {originalCat} → {newCats.length} {newCats.length === 1 ? 'Category' : 'Categories'}
                          </div>
                          <div className="text-sm text-white/50">{totalPosts} posts</div>
                        </div>
                        {allDone
                          ? <span className="px-3 py-1 text-xs font-bold rounded-full bg-[#22c55e] text-white shrink-0">✓ DONE</span>
                          : <span className={`px-3 py-1 text-xs font-bold rounded-full shrink-0 ${getPriorityBadge(rec.action_priority)}`}>SPLIT</span>
                        }
                      </div>

                      {/* Inline disposition prompt (HubSpot only, shown once per original tag) */}
                      {showPromptHere && (
                        <div className="mx-4 mt-4 p-4 bg-[var(--bg-tertiary)] rounded-lg border border-[#f59e0b]/40">
                          <p className="text-sm font-medium mb-3">
                            Before creating <span className="text-[#ffdc18]">&ldquo;{dispositionPrompt!.newTagName}&rdquo;</span> —
                            what should happen to the original <span className="text-white">&ldquo;{dispositionPrompt!.originalCatName}&rdquo;</span> tag?
                          </p>
                          <div className="space-y-2 mb-3">
                            {(['keep', 'mark_structural', 'remove'] as const).map(opt => (
                              <label key={opt} className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tagAction-${recIndex}-${implKey}`}
                                  checked={pendingTagAction === opt}
                                  onChange={() => setPendingTagAction(opt)}
                                  className="mt-0.5 accent-[#ffdc18]"
                                />
                                <span className="text-sm">
                                  {opt === 'keep' && 'Keep on all posts (no change)'}
                                  {opt === 'mark_structural' && (
                                    <><span className="text-[#ffdc18]">●</span> Keep and mark as Structural in VizzEx <span className="text-white/40">(recommended)</span></>
                                  )}
                                  {opt === 'remove' && 'Remove from all posts'}
                                </span>
                              </label>
                            ))}
                          </div>
                          {pendingTagAction !== 'keep' && (
                            <div className="mb-3">
                              <label className="text-xs text-white/50 block mb-1">Select the original HubSpot tag:</label>
                              {tagsLoading
                                ? <div className="flex items-center gap-2 text-xs text-white/50"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                                : (
                                  <select
                                    value={pendingTagId}
                                    onChange={e => setPendingTagId(e.target.value)}
                                    className="w-full px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded text-sm focus:border-[#ffdc18] focus:outline-none"
                                  >
                                    <option value="">— Select tag —</option>
                                    {availableTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                  </select>
                                )
                              }
                            </div>
                          )}
                          {implementError && <p className="text-sm text-[#ef4444] mb-2">{implementError}</p>}
                          <div className="flex gap-2">
                            <button
                              onClick={handleDispositionConfirm}
                              disabled={implementingKey !== null}
                              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded text-sm font-medium disabled:opacity-50 transition-colors"
                            >
                              {implementingKey !== null && <Loader2 className="w-3 h-3 animate-spin" />}
                              Confirm &amp; Implement
                            </button>
                            <button
                              onClick={() => setDispositionPrompt(null)}
                              className="px-4 py-1.5 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] rounded text-sm transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* New category cards */}
                      <div className="p-4 space-y-3">
                        {newCats.map((nc, catIndex) => {
                          const catKey = makeCatKey(recIndex, implKey, catIndex);
                          const done = isNewCatDone(originalCat, nc.category_name);
                          const postCount = nc.post_count ?? nc.post_ids?.length ?? 0;
                          const postTitleEntries = Object.entries(nc.post_titles || {});
                          const isImplThis = implementingKey === catKey;
                          const isManualThis = manuallyApplyingKey === catKey;

                          return (
                            <div key={catIndex} className="rounded-lg border border-[var(--bg-tertiary)] overflow-hidden">
                              {/* New-category header */}
                              <div className="px-3 py-2.5 bg-[var(--bg-secondary)] flex items-center gap-3">
                                <span className="px-2 py-0.5 text-xs font-bold rounded bg-[#3b82f6] text-white shrink-0">NEW</span>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm">{nc.category_name}</div>
                                  <div className="text-xs text-white/40">{postCount} posts [new category]</div>
                                </div>
                                {done && <CheckCircle2 className="w-4 h-4 text-[#22c55e] shrink-0" />}
                              </div>

                              <div className="px-3 py-2 space-y-2">
                                {/* Rationale */}
                                {nc.description && (
                                  <div>
                                    <button
                                      onClick={() => toggleRationale(catKey)}
                                      className="text-xs text-[#3b82f6] hover:underline flex items-center gap-1"
                                    >
                                      <span>{expandedRationale.has(catKey) ? '▼' : '▶'}</span>
                                      {expandedRationale.has(catKey) ? 'Hide Rationale' : 'Show Rationale'}
                                    </button>
                                    {expandedRationale.has(catKey) && (
                                      <div className="mt-1.5 px-3 py-2 bg-[var(--bg-tertiary)] rounded text-xs text-[var(--text-secondary)] italic">
                                        {nc.description}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Posts */}
                                {postCount > 0 && (
                                  <div>
                                    <button
                                      onClick={() => togglePosts(catKey)}
                                      className="text-xs text-[#3b82f6] hover:underline flex items-center gap-1"
                                    >
                                      <span>{expandedPosts.has(catKey) ? '▼' : '▶'}</span>
                                      {expandedPosts.has(catKey) ? `Hide Posts (${postCount})` : `Show Posts (${postCount})`}
                                    </button>
                                    {expandedPosts.has(catKey) && (
                                      <div className="mt-1.5 border border-[var(--bg-tertiary)] rounded overflow-hidden max-h-64 overflow-y-auto">
                                        {postTitleEntries.length > 0 ? (
                                          <ul className="divide-y divide-[var(--bg-tertiary)]">
                                            {postTitleEntries.map(([id, title]) => (
                                              <li key={id} className="px-3 py-1.5 text-xs text-[var(--text-secondary)]">
                                                {title} <span className="text-white/25">(#{id})</span>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div className="px-3 py-2 text-xs text-white/35 italic">
                                            {nc.post_ids?.length
                                              ? `Post IDs: ${nc.post_ids.slice(0, 10).join(', ')}${nc.post_ids.length > 10 ? '…' : ''}`
                                              : 'Post titles not available — re-fetch results to load them'}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Action buttons */}
                                {done ? (
                                  <div className="flex items-center gap-1.5 pt-1 text-xs text-[#22c55e]">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Implemented
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[var(--bg-tertiary)]">
                                    {isHtmlSite ? (
                                      <span className="text-xs text-white/35 italic">ℹ️ Apply this tag change in your CMS</span>
                                    ) : (
                                      <button
                                        onClick={() => handleImplementClick(recIndex, implKey, catIndex, originalCat, nc.category_name, nc.post_ids || [])}
                                        disabled={isImplThis || dispositionPrompt !== null}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded text-xs font-medium disabled:opacity-50 transition-colors"
                                      >
                                        {isImplThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                        Implement This Category
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleManuallyApplied(recIndex, implKey, catIndex, originalCat, nc.category_name)}
                                      disabled={isManualThis || manualApplyPrompt !== null}
                                      className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--bg-tertiary)] text-white/55 rounded text-xs font-medium hover:bg-[var(--bg-tertiary)] disabled:opacity-50 transition-colors"
                                    >
                                      {isManualThis && <Loader2 className="w-3 h-3 animate-spin" />}
                                      Manually Implemented
                                    </button>
                                  </div>
                                )}

                                {/* Inline manual-apply prompt — confirm/edit the real CMS category name */}
                                {manualApplyPrompt?.recIndex === recIndex &&
                                  manualApplyPrompt?.implKey === implKey &&
                                  manualApplyPrompt?.catIndex === catIndex && (
                                  <div className="mt-2 p-3 bg-[var(--bg-tertiary)] rounded-lg border border-[#f59e0b]/40">
                                    <label className="text-xs text-white/50 block mb-1">
                                      What did you actually name this category in your CMS?
                                    </label>
                                    <input
                                      type="text"
                                      value={manualCategoryName}
                                      onChange={e => setManualCategoryName(e.target.value)}
                                      className="w-full px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded text-sm focus:border-[#ffdc18] focus:outline-none"
                                      placeholder={nc.category_name}
                                    />
                                    {implementError && <p className="text-xs text-[#ef4444] mt-2">{implementError}</p>}
                                    <div className="flex gap-2 mt-2">
                                      <button
                                        onClick={handleManualApplyConfirm}
                                        disabled={!manualCategoryName.trim()}
                                        className="px-3 py-1.5 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded text-xs font-medium disabled:opacity-50 transition-colors"
                                      >
                                        Confirm
                                      </button>
                                      <button
                                        onClick={() => setManualApplyPrompt(null)}
                                        className="px-3 py-1.5 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] rounded text-xs transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })}
            </div>
          )}

          {/* Analysis Metadata Summary */}
          {data.analysis_metadata && (
            <div className="glass rounded-lg overflow-hidden">
              <div className="p-3 bg-[var(--bg-tertiary)] flex items-center gap-3">
                <span className="text-2xl">📈</span>
                <div className="flex-1">
                  <div className="font-semibold">Analysis Summary</div>
                  <div className="text-xs text-white/70">Content Structure Overview</div>
                </div>
                <span className="px-2 py-0.5 text-xs font-bold rounded bg-[#3b82f6] text-white">SUMMARY</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {data.analysis_metadata.total_posts !== undefined && (
                    <div className="text-center p-3 bg-[var(--bg-tertiary)] rounded">
                      <div className="text-2xl font-bold text-[#3b82f6]">{data.analysis_metadata.total_posts}</div>
                      <div className="text-xs text-white/80">Total Posts</div>
                    </div>
                  )}
                  {data.analysis_metadata.categories_requiring_splits !== undefined && (
                    <div className="text-center p-3 bg-[var(--bg-tertiary)] rounded">
                      <div className="text-2xl font-bold text-[#ef4444]">{data.analysis_metadata.categories_requiring_splits}</div>
                      <div className="text-xs text-white/80">Need Splitting</div>
                    </div>
                  )}
                  {data.analysis_metadata.categories_ready_for_optimization !== undefined && (
                    <div className="text-center p-3 bg-[var(--bg-tertiary)] rounded">
                      <div className="text-2xl font-bold text-[#22c55e]">{data.analysis_metadata.categories_ready_for_optimization}</div>
                      <div className="text-xs text-white/80">Ready for Optimization</div>
                    </div>
                  )}
                  {data.analysis_metadata.confidence_level && (
                    <div className="text-center p-3 bg-[var(--bg-tertiary)] rounded">
                      <div className={`text-xl font-bold capitalize ${
                        data.analysis_metadata.confidence_level === 'high' ? 'text-[#22c55e]' :
                        data.analysis_metadata.confidence_level === 'medium' ? 'text-[#f59e0b]' : 'text-[#ef4444]'
                      }`}>
                        {data.analysis_metadata.confidence_level}
                      </div>
                      <div className="text-xs text-white/80">Confidence Level</div>
                    </div>
                  )}
                </div>
                {data.analysis_metadata.primary_strategy_rationale && (
                  <div className="mt-4 border-l-4 border-[#3b82f6] pl-3">
                    <h6 className="text-sm font-medium text-[#3b82f6] mb-1">Strategic Rationale</h6>
                    <p className="text-sm italic text-[var(--text-secondary)]">{data.analysis_metadata.primary_strategy_rationale}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {recommendationCount === 0 && !data.strategic_assessment && !data.recommended_strategy && (
            <div className="p-6 text-center text-white/70">
              <p className="italic">Strategic Category Recommendations will be generated during your next baseline analysis.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Markdown Content Component - Reusable
// Renders markdown content with proper styling - readable against dark background
interface MarkdownContentProps {
  content: string;
}

// Convert inline markdown to HTML
function parseInlineMarkdown(text: string): string {
  return text
    // Bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
    .replace(/__(.+?)__/g, '<strong class="font-semibold text-white">$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    .replace(/_(.+?)_/g, '<em class="italic">$1</em>')
    // Inline code: `code`
    .replace(/`(.+?)`/g, '<code class="px-1.5 py-0.5 bg-white/10 rounded text-[#ffdc18] font-mono text-xs">$1</code>');
}

function MarkdownContent({ content }: MarkdownContentProps) {
  // Parse the content into sections
  const sections = useMemo(() => {
    type Section = 
      | { type: 'heading'; content: string }
      | { type: 'subheading'; content: string }
      | { type: 'paragraph'; content: string }
      | { type: 'list'; items: string[]; ordered: boolean };
    
    const result: Section[] = [];
    const lines = content.split('\n');
    let currentParagraph: string[] = [];
    let currentList: { items: string[]; ordered: boolean } | null = null;
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const text = currentParagraph.join(' ').trim();
        if (text) {
          result.push({ type: 'paragraph', content: text });
        }
        currentParagraph = [];
      }
    };
    
    const flushList = () => {
      if (currentList && currentList.items.length > 0) {
        result.push({ type: 'list', items: currentList.items, ordered: currentList.ordered });
        currentList = null;
      }
    };
    
    lines.forEach(line => {
      const trimmed = line.trim();
      
      // Check for headings
      if (trimmed.startsWith('# ')) {
        flushParagraph();
        flushList();
        result.push({ type: 'heading', content: trimmed.replace(/^#\s*/, '') });
      } else if (trimmed.startsWith('## ') || trimmed.startsWith('### ')) {
        flushParagraph();
        flushList();
        result.push({ type: 'subheading', content: trimmed.replace(/^#+\s*/, '') });
      } 
      // Check for unordered list items
      else if (trimmed.match(/^[-*•]\s+/)) {
        flushParagraph();
        if (!currentList || currentList.ordered) {
          flushList();
          currentList = { items: [], ordered: false };
        }
        currentList.items.push(trimmed.replace(/^[-*•]\s+/, ''));
      }
      // Check for ordered list items
      else if (trimmed.match(/^\d+[.)]\s+/)) {
        flushParagraph();
        if (!currentList || !currentList.ordered) {
          flushList();
          currentList = { items: [], ordered: true };
        }
        currentList.items.push(trimmed.replace(/^\d+[.)]\s+/, ''));
      }
      // Empty line
      else if (trimmed === '') {
        flushParagraph();
        flushList();
      } 
      // Regular text
      else {
        flushList();
        currentParagraph.push(trimmed);
      }
    });
    
    flushParagraph();
    flushList();
    return result;
  }, [content]);
  
  return (
    <div className="space-y-4 bg-[var(--bg-tertiary)] rounded-lg p-5">
      {sections.map((section, i) => {
        if (section.type === 'heading') {
          return (
            <h2 key={i} className="text-xl font-bold text-white mb-3">
              {section.content}
            </h2>
          );
        }
        
        if (section.type === 'subheading') {
          return (
            <div key={i} className="mt-5 mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#ffdc18] border-b border-[#ffdc18]/30 pb-2">
                {section.content}
              </h3>
            </div>
          );
        }
        
        if (section.type === 'list') {
          const ListTag = section.ordered ? 'ol' : 'ul';
          return (
            <ListTag 
              key={i} 
              className={`text-sm text-white/90 leading-relaxed space-y-2 ml-4 ${
                section.ordered ? 'list-decimal' : 'list-disc'
              }`}
            >
              {section.items.map((item, j) => (
                <li 
                  key={j} 
                  className="pl-1"
                  dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }}
                />
              ))}
            </ListTag>
          );
        }
        
        return (
          <p 
            key={i} 
            className="text-sm text-white/90 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(section.content) }}
          />
        );
      })}
    </div>
  );
}

/**
 * Analysis Paused Banner Component
 * Displayed when analysis is paused due to billing error, throttling, API overload, etc.
 */
interface AnalysisPausedBannerProps {
  pauseReason: {
    type: 'billing' | 'throttling' | 'overload' | 'stuck' | 'unknown';
    message: string;
    details?: string;
    actionUrl?: string;
    canResume: boolean;
  };
  onResume: () => void;
  onCancel: () => void;
  isResuming: boolean;
  sessionId: string;
  progress: number;
  progressMessage?: string;
}

function AnalysisPausedBanner({ pauseReason, onResume, onCancel, isResuming, sessionId, progress, progressMessage }: AnalysisPausedBannerProps) {
  // Get appropriate header based on pause type
  const getHeaderInfo = () => {
    switch (pauseReason.type) {
      case 'billing':
        return {
          icon: '💳',
          title: 'Analysis Paused - API Credits Depleted',
          color: '#f59e0b' // amber
        };
      case 'throttling':
        return {
          icon: '⏱️',
          title: 'Analysis Paused - Rate Limit Reached',
          color: '#3b82f6' // blue
        };
      case 'overload':
        return {
          icon: '🔄',
          title: 'Analysis Paused - API Temporarily Overloaded',
          color: '#8b5cf6' // purple
        };
      case 'stuck':
        return {
          icon: '⏱️',
          title: 'Analysis Stalled',
          color: '#d97706' // amber-600
        };
      default:
        return {
          icon: '⏸️',
          title: 'Analysis Paused',
          color: '#6b7280' // gray
        };
    }
  };

  const headerInfo = getHeaderInfo();

  // Get instructions based on pause type
  const getInstructions = () => {
    switch (pauseReason.type) {
      case 'billing':
        return (
          <div className="space-y-3">
            <ol className="text-sm text-white/80 space-y-1 list-decimal ml-4">
              <li>Add credits to your Anthropic account</li>
              <li>Return here and click "Resume Analysis"</li>
            </ol>
            <a 
              href={pauseReason.actionUrl || 'https://console.anthropic.com'}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#d97706] hover:bg-[#b45309] text-white font-medium rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Add Credits to Anthropic Account
            </a>
          </div>
        );
      case 'throttling':
        return (
          <p className="text-sm text-white/80">
            Your API rate limit has been reached. Wait a few moments, then click "Resume Analysis" to continue.
          </p>
        );
      case 'overload':
        return (
          <p className="text-sm text-white/80">
            The Claude API is experiencing high demand. Wait a few moments, then click "Resume Analysis" to continue.
          </p>
        );
      case 'stuck':
        return (
          <div className="space-y-3">
            <p className="text-sm text-white/80">
              The analysis hasn't progressed for a while. This can happen due to:
            </p>
            <ul className="text-sm text-white/70 list-disc ml-4 space-y-1">
              <li>Temporary network issues</li>
              <li>Server processing delays</li>
              <li>High system load</li>
            </ul>
            <p className="text-sm text-white/80">
              Click <strong>Resume Analysis</strong> to reconnect and continue from where it stopped.
            </p>
          </div>
        );
      default:
        return (
          <p className="text-sm text-white/80">
            Click "Resume Analysis" to attempt to continue from where it left off.
          </p>
        );
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[var(--text-secondary)]">
        Your analysis has been paused.
      </p>

      {/* Paused Analysis Card */}
      <div 
        className="border rounded-lg overflow-hidden"
        style={{ 
          borderColor: `${headerInfo.color}66`,
          backgroundColor: `${headerInfo.color}1a`
        }}
      >
        {/* Header */}
        <div 
          className="px-4 py-3 border-b flex items-center justify-between"
          style={{ 
            backgroundColor: `${headerInfo.color}33`,
            borderColor: `${headerInfo.color}4d`
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">{headerInfo.icon}</span>
            <span className="font-semibold" style={{ color: headerInfo.color }}>
              {headerInfo.title}
            </span>
          </div>
          <span 
            className="px-2 py-0.5 rounded text-xs font-medium"
            style={{ 
              backgroundColor: `${headerInfo.color}4d`,
              color: headerInfo.color
            }}
          >
            Paused
          </span>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          <p className="text-white/90">
            {pauseReason.message}
          </p>
          
          {/* Show details if different from message */}
          {pauseReason.details && pauseReason.details !== pauseReason.message && (
            <p className="text-sm text-white/70 bg-[var(--bg-tertiary)] px-3 py-2 rounded font-mono">
              {pauseReason.details}
            </p>
          )}
          
          {/* Session Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-white/70">Session ID</span>
              <p className="font-mono text-xs mt-1 bg-[var(--bg-tertiary)] px-2 py-1 rounded truncate">
                {sessionId}
              </p>
            </div>
            <div>
              <span className="text-white/70">Progress when paused</span>
              <p className="mt-1 font-medium">{progress}%</p>
            </div>
          </div>
          
          {/* Progress message if available */}
          {progressMessage && (
            <div className="text-sm">
              <span className="text-white/70">Last status: </span>
              <span className="text-white/90">{progressMessage}</span>
            </div>
          )}
          
          {/* Instructions */}
          <div className="p-3 bg-[var(--bg-tertiary)] rounded-lg">
            <p className="text-sm text-white/70 mb-2">
              To continue this analysis:
            </p>
            {getInstructions()}
          </div>
          
          <p className="text-xs text-white/50 italic">
            💡 Your analysis will continue from where it left off - no data will be lost.
          </p>
        </div>
        
        {/* Actions */}
        <div className="px-4 py-3 bg-[var(--bg-tertiary)]/50 border-t border-[var(--bg-tertiary)] flex justify-between">
          <button
            onClick={onCancel}
            disabled={isResuming}
            className="px-4 py-2 bg-[var(--color-error)]/10 text-[var(--color-error)] rounded-lg hover:bg-[var(--color-error)]/20 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Cancel Analysis
          </button>
          <button
            onClick={onResume}
            disabled={isResuming || !pauseReason.canResume}
            className="px-4 py-2 bg-[#ffdc18] hover:bg-[#e6c616] text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isResuming ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Resuming...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Resume Analysis
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hub Posts Overview Component
// Displays each hub post with progress toward Content Hub tier
function HubPostsOverviewSection({ 
  hubPosts, 
  platformId 
}: { 
  hubPosts: HubPostOverview[];
  platformId?: string;
}) {
  if (!hubPosts || hubPosts.length === 0) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'strong':
        return { bg: 'bg-[#4ade80]/20', border: 'border-[#4ade80]', text: 'text-[#4ade80]' };
      case 'adequate':
        return { bg: 'bg-[#60a5fa]/20', border: 'border-[#60a5fa]', text: 'text-[#60a5fa]' };
      case 'under_connected':
      default:
        return { bg: 'bg-[#fbbf24]/20', border: 'border-[#fbbf24]', text: 'text-[#fbbf24]' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'strong': return 'Strong';
      case 'adequate': return 'Adequate';
      case 'under_connected':
      default: return 'Needs Attention';
    }
  };

  return (
    <div className="mt-6 bg-[var(--bg-secondary)] rounded-lg border border-[var(--bg-tertiary)] overflow-hidden">
      <div className="p-4 border-b border-[var(--bg-tertiary)] bg-[var(--bg-tertiary)]/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🏛️</span>
            <h3 className="font-semibold">Hub Posts Status</h3>
          </div>
          <span className="text-sm text-[var(--text-secondary)]">
            {hubPosts.length} hub{hubPosts.length !== 1 ? 's' : ''} identified
          </span>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Track your hub posts' progress toward the Content Hub tier (25 points)
        </p>
      </div>

      <div className="divide-y divide-[var(--bg-tertiary)]">
        {hubPosts.map((hub) => {
          const progressPercent = Math.min(100, Math.round((hub.currentPoints / hub.targetPoints) * 100));
          const statusColors = getStatusColor(hub.hubStatus);
          
          // Build HubSpot edit URL
          const editUrl = platformId 
            ? `https://app.hubspot.com/content/${platformId}/blog/editor/${hub.postId}`
            : null;

          return (
            <div key={hub.postId} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Category Badge */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs font-medium bg-[#a855f7]/20 text-[#a855f7] rounded">
                      {hub.hubCategory}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColors.bg} ${statusColors.text}`}>
                      {getStatusLabel(hub.hubStatus)}
                    </span>
                  </div>
                  
                  {/* Post Title */}
                  <div className="flex items-center gap-2">
                    {editUrl ? (
                      <a 
                        href={editUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-[var(--text-primary)] hover:text-[#ffdc18] transition-colors truncate"
                        title={hub.postTitle}
                      >
                        {hub.postTitle}
                      </a>
                    ) : (
                      <span className="font-medium text-[var(--text-primary)] truncate" title={hub.postTitle}>
                        {hub.postTitle}
                      </span>
                    )}
                    {editUrl && (
                      <ExternalLink className="w-3.5 h-3.5 text-[var(--text-secondary)] flex-shrink-0" />
                    )}
                  </div>
                </div>

                {/* Progress Stats */}
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold">
                    <span className={hub.currentPoints >= hub.targetPoints ? 'text-[#4ade80]' : 'text-[var(--text-primary)]'}>
                      {Math.round(hub.currentPoints)}
                    </span>
                    <span className="text-[var(--text-secondary)]"> / {hub.targetPoints}</span>
                  </div>
                  <div className="text-xs text-[var(--text-secondary)]">points</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="flex justify-between text-xs text-[var(--text-secondary)] mb-1">
                  <span>Progress to Content Hub</span>
                  <span className={progressPercent >= 100 ? 'text-[#4ade80] font-medium' : ''}>
                    {progressPercent}%
                  </span>
                </div>
                <div className="h-2 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 rounded-full ${
                      progressPercent >= 100 
                        ? 'bg-[#4ade80]' 
                        : progressPercent >= 60 
                          ? 'bg-[#60a5fa]' 
                          : 'bg-[#fbbf24]'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
